import { lazy, Suspense, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  Clock,
  Activity,
  ArrowLeft,
  Ban,
  CircleCheck,
  Cloud,
  Cpu,
  DatabaseBackup,
  Download,
  Eye,
  EyeOff,
  History,
  Link2,
  Pencil,
  Power,
  Radar,
  RefreshCw,
  RotateCcw,
  Search,
  Server,
  Terminal,
  Trash2,
} from 'lucide-react';
import {
  compressVlanRanges,
  eventNotificationsOn,
  onuHealthLabel,
  onuHealthTone,
  signalBand,
  type CliActionResult,
  type DeferredDeauthorization,
  type Olt,
  type OltBackup,
  type OltCard,
  type OltCredentials,
  type OltDeletePlan,
  type OltDeleteResult,
  type OltHealth,
  type OltProbe,
  type OltFanUnit,
  type OltStatReading,
  type OltStats,
  type JobHandle,
  type OltSyncResult,
  type OltTr069Profile,
  type Onu,
  type Page,
  type PonPort,
  type RestoreJob,
  type SnmpWalkRow,
  type Tr069Profile,
  type UplinkPort,
  type UplinkVlanSummary,
} from '@simpleacs/shared';
import { OltVlansTab } from './olt/VlansTab';
import { SpeedProfilesTab } from './olt/SpeedProfilesTab';
import { OltHostKeyCard } from './olt/HostKeyCard';
import { LinkDevicesDialog } from './olt/LinkDevicesDialog';
import { ManagementNtpDialog } from './olt/ManagementNtpDialog';
import { PonPortOpticsDialog } from './olt/PonPortOpticsDialog';
import { OltImageBlock } from './olt/OltImage';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { WizardDialog } from '../components/WizardDialog';
import {
  DisableDialog,
  DisabledBanner,
  DisabledPill,
  useSetDisabled,
} from '../components/OutOfService';
import { api } from '../lib/api';
import { runJob } from '../lib/jobs';
import { onuPath } from '../lib/links';
import { useTasks } from '../lib/tasks';
import { formatLatency, formatRelative, formatTimestamp, formatUptime, platformIsoDate } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Pill,
  Spinner,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';

// The terminal library is fetched only when someone opens the Console tab.
const ConsoleTab = lazy(() => import('./olt/ConsoleTab').then((m) => ({ default: m.ConsoleTab })));

type Tab =
  | 'details'
  | 'cards'
  | 'pon'
  | 'uplinks'
  | 'vlans'
  | 'speeds'
  | 'onus'
  | 'events'
  | 'backups'
  | 'console'
  | 'diagnostics';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'cards', label: 'Cards' },
  { id: 'pon', label: 'PON ports' },
  { id: 'uplinks', label: 'Uplinks' },
  { id: 'vlans', label: 'VLANs' },
  { id: 'speeds', label: 'Speed profiles' },
  { id: 'onus', label: 'ONUs' },
  { id: 'events', label: 'Events' },
  { id: 'backups', label: 'Backups' },
  { id: 'console', label: 'Console' },
  { id: 'diagnostics', label: 'Diagnostics' },
];

export function OltDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useSession();
  /**
   * `?tab=` so another page can send an operator straight to the right tab —
   * the OLT list's Events column does exactly that. Validated against the tab
   * list rather than cast, so a hand-edited or stale URL lands on Details
   * instead of rendering nothing. Read once, on mount: clicking a tab after
   * that stays local state, so the URL never fights the click.
   */
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const wanted = searchParams.get('tab');
    return TABS.some((t) => t.id === wanted) ? (wanted as Tab) : 'details';
  });
  const [notice, setNotice] = useState<string | null>(null);
  const tasks = useTasks();
  const [warnings, setWarnings] = useState<string[]>([]);
  const [editing, setEditing] = useState(false);
  /** The reason prompt, open only on the way OUT of service. */
  const [disabling, setDisabling] = useState(false);
  const [linking, setLinking] = useState(false);
  const [removing, setRemoving] = useState(false);

  const oltQuery = useQuery({
    queryKey: ['olt', id],
    queryFn: () => api.get<Olt>(`/olts/${encodeURIComponent(id)}`),
    refetchInterval: 30_000,
  });

  const probe = useMutation({
    mutationFn: () => api.post<OltProbe>(`/olts/${encodeURIComponent(id)}/probe`),
    onSuccess: async (result) => {
      setNotice(
        result.reachable
          ? `Reachable — SNMP ${result.snmp.ok ? 'ok' : 'failed'}, CLI ${result.cli.ok ? 'ok' : 'failed'}${
              result.detectedModel ? `, detected ${result.detectedModel}` : ''
            }`
          : `Unreachable — ${result.snmp.error ?? result.cli.error ?? 'no response'}`,
      );
      await queryClient.invalidateQueries({ queryKey: ['olt', id] });
    },
    onError: (err) => setNotice(err instanceof Error ? err.message : 'Probe failed'),
  });

  /**
   * Import ONUs: the operator's one gesture for "make SimpleACS hold this
   * chassis". Step one is the same inventory sync as the Sync button — it
   * CREATES every ONU the chassis has that the platform does not, updates
   * the rest, and its occupancy rules are what "do not duplicate" means
   * here. Step two hands over to a bulk run of the read-rung import action,
   * which walks every ONU recording its config; the navigation lands on the
   * bulk page with this chassis and that action already chosen.
   */
  /**
   * Import, whose FIRST step is that same sweep — so it runs in the
   * background too, and the hand-off to the bulk page happens when the
   * inventory is actually there rather than while it is still being read.
   */
  const startImport = () => {
    tasks.start<OltSyncResult>({
      label: `Import ONUs from ${olt?.name ?? 'chassis'}`,
      /*
        The ROW id, not `id` — `id` is the OLT's short NUMBER off the URL, and
        the bulk page's chassis <select> is keyed by row id and `bulkOltFor`
        does `eq(olts.id, …)` with no number fallback (§8.3). Passing the number
        left the selector on "Choose a chassis", Matching ONUs at 0, and Build
        disabled forever, while the sync itself worked (it resolves a number).
      */
      href: `/bulk-actions?olt=${encodeURIComponent(olt?.id ?? id)}&action=onu_adopt`,
      run: () =>
        api
          .post<JobHandle>(`/olts/${encodeURIComponent(id)}/sync`)
          .then((handle) => runJob<OltSyncResult>(handle)),
      done: (result) =>
        `${result.onus} ONUs listed. Open the import run to record each one's config.`,
      invalidate: [
        ['olt', id],
        ['olt', id, 'onus'],
      ],
    });
  };

  /**
   * The inventory sweep, as BACKGROUND work.
   *
   * It is the slowest thing this page starts: SNMP and CLI across every card
   * and port, minutes on a full shelf. Waiting for it behind a spinner meant
   * an operator could not do anything else with the chassis they had just
   * asked about — and closing the tab lost the result of a sweep the platform
   * finished anyway. The toast reports it, wherever they have gone.
   *
   * The warnings a sweep collects are the one thing a toast cannot carry, so
   * the refreshed page shows them: the invalidation below is what puts them
   * on screen.
   */
  const startSync = () => {
    tasks.start<OltSyncResult>({
      label: `Sync ${olt?.name ?? 'chassis'}`,
      href: `/olts/${encodeURIComponent(id)}`,
      // Enqueued server-side and polled, so a full sweep of a slow shelf never
      // 504s at the proxy.
      run: () =>
        api
          .post<JobHandle>(`/olts/${encodeURIComponent(id)}/sync`)
          .then((handle) => runJob<OltSyncResult>(handle)),
      done: (result) =>
        `${result.ponPorts} PON ports and ${result.onus} ONUs, ${result.linkedDevices} linked to TR-069${
          result.warnings.length ? ` — ${result.warnings.length} warning(s) on the page` : ''
        }.`,
      invalidate: [
        ['olt', id],
        ['olt', id, 'pon'],
        ['olt', id, 'onus'],
      ],
    });
  };

  /**
   * Only ever called to put the chassis BACK in service. The other direction
   * goes through the dialog, which owns its own copy of this and the reason
   * that has to be typed into it — enabling is not destructive, clears the
   * reason server-side, and asking "are you sure you want to resume polling"
   * is a question with one answer.
   */
  const enable = useSetDisabled({
    endpoint: `/olts/${encodeURIComponent(id)}/disabled`,
    onDone: setNotice,
    onError: setNotice,
  });

  if (oltQuery.isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (oltQuery.error) return <ErrorNotice error={oltQuery.error} />;
  const olt = oltQuery.data;
  if (!olt) return null;

  return (
    <>
      <button
        onClick={() => navigate('/olts')}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Back to OLTs
      </button>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            {olt.number != null && (
              <span className="font-mono text-sm text-faint tabular-nums">#{olt.number}</span>
            )}
            <h1 className="text-xl font-semibold text-ink">{olt.name}</h1>
            <Pill state
              tone={olt.status === 'up' ? 'success' : olt.status === 'down' ? 'danger' : 'neutral'}
              dot
            >
              {olt.status}
            </Pill>
            <Pill tone="primary">{olt.vendor.toUpperCase()}</Pill>
            {/* Beside `status`, and saying a different kind of thing. `status`
                is what the last poll found; this is what a person decided —
                and while it stands, nothing is polling, so the pill next to it
                is a reading that has stopped moving rather than a live one. */}
            {olt.disabledAt ? (
              <DisabledPill kind="olt" disabledReason={olt.disabledReason} />
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted">
            {[olt.model, olt.softwareVersion, olt.mgmtIp].filter(Boolean).join(' · ')}
          </p>
          {/* Reachability at a glance: the page paints from the stored record
              even when the chassis cannot be reached, so it must SAY when what
              it shows is not live. Gated on `status === 'down'` ALONE, not on
              `lastError`: that field holds the sweep's notes as often as its
              failures ("Read over the CLI: 1 ONU across 32 ports", "4 ONU state
              change(s) recorded"), so keying "Unreachable" off it painted a red
              alarm on a chassis that answered fine. The Dashboard fixed the same
              bug; this banner had kept it. `status` is the honest signal. */}
          {olt.status === 'down' && !olt.disabledAt ? (
            <p className="mt-1 text-sm text-danger">
              Unreachable{olt.lastError ? ` — ${olt.lastError}` : ''}
            </p>
          ) : null}
          {olt.lastPolledAt ? (
            <p className="mt-0.5 text-xs text-faint">
              Last reached {formatRelative(olt.lastPolledAt)}
            </p>
          ) : null}
        </div>

        {/*
          Two rungs share this row since the 2026-08-30 split. Reading the
          chassis — Test connection, Sync inventory, Import ONUs — is
          `olt.diagnose`, which an engineer holds; editing, disabling, deleting
          and pairing records are `olt.manage`, which they do not. Rendering one
          row and hiding the halves separately is what keeps an engineer from
          seeing an empty header where their two most-used buttons live.
        */}
        {(can('olt.manage') || can('olt.diagnose')) && (
          <div className="flex flex-wrap gap-2">
            {can('olt.manage') && (
              <Button icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {/* Both of these read the chassis, which a disabled one is not
                meant to be. The API refuses both on a disabled OLT now —
                `probe` always did, `sync` since the guard moved into the
                service — so greying them here only saves a round trip to be
                told what the banner above already says. */}
            <Button
              icon={<Radar className="size-4" />}
              loading={probe.isPending}
              disabled={Boolean(olt.disabledAt)}
              title={
                olt.disabledAt
                  ? `${olt.name} is out of service in SimpleACS, so nothing is read from it. Enable it first.`
                  : undefined
              }
              onClick={() => probe.mutate()}
            >
              Test connection
            </Button>
            <Button
              variant="primary"
              icon={<RefreshCw className="size-4" />}
              loading={false}
              disabled={Boolean(olt.disabledAt)}
              title={
                olt.disabledAt
                  ? `${olt.name} is out of service in SimpleACS, so nothing is read from it. Enable it first.`
                  : undefined
              }
              onClick={startSync}
            >
              Sync inventory
            </Button>
            <Button
              icon={<Download className="size-4" />}
              loading={false}
              disabled={Boolean(olt.disabledAt)}
              title={
                olt.disabledAt
                  ? `${olt.name} is out of service in SimpleACS, so nothing is read from it. Enable it first.`
                  : 'Make SimpleACS hold this chassis: sync the inventory (creating any ONUs not yet listed — never duplicating), then review a bulk run that reads every ONU’s config off the chassis and records it here. Nothing is written to the chassis.'
              }
              onClick={startImport}
            >
              Import ONUs
            </Button>
            {/*
              ZTE only: the serial mismatch this repairs is the F670L announcing
              a router serial to TR-069. On Huawei the serials agree and the
              sweep's own match already links everything — the API refuses the
              run there, so the button never appears to offer it.
            */}
            {olt.vendor === 'zte' && can('olt.manage') && (
              <Button
                icon={<Link2 className="size-4" />}
                disabled={Boolean(olt.disabledAt)}
                title={
                  olt.disabledAt
                    ? `${olt.name} is out of service in SimpleACS, so nothing is read from it. Enable it first.`
                    : 'Pair ONUs with ACS devices whose TR-069 serial differs from the chassis one — matched by management address, previewed before anything is written'
                }
                onClick={() => setLinking(true)}
              >
                Link ACS devices
              </Button>
            )}
            {/*
              Last in the row, and in the platform's own colours for this pair:
              red disables, green enables. The red is why the dialog behind it
              has to carry the reassurance — red beside a chassis name reads as
              "this cuts people off", and that is the one thing disabling does
              not do. Enabling needs no such warning and no confirmation.
            */}
            {!can('olt.manage') ? null : olt.disabledAt ? (
              <Button
                variant="success"
                icon={<CircleCheck className="size-4" />}
                loading={enable.isPending}
                title="Put this chassis back in service — polling, sweeps, backups, bulk runs and writes all resume"
                onClick={() => enable.mutate({ disabled: false })}
              >
                Enable
              </Button>
            ) : (
              <Button
                variant="danger"
                icon={<Ban className="size-4" />}
                title="Stop SimpleACS acting on this chassis — polling, sweeps, backups, bulk runs and writes. Subscriber service is unaffected."
                onClick={() => setDisabling(true)}
              >
                Disable
              </Button>
            )}
            {/*
              Delete takes the whole record with it — ONUs, backups, the ACS's
              CPE records — which is why the dialog behind this button leads
              with the exact footprint and demands the chassis name typed
              back. What it never touches is the chassis itself: no CLI, no
              config change, no subscriber affected.
            */}
            {can('olt.manage') && (
              <Button
                variant="danger"
                icon={<Trash2 className="size-4" />}
                title="Remove this chassis and everything recorded under it — ONUs, config backups, and its CPE records on the ACS. Nothing is sent to the OLT itself; subscriber service is untouched."
                onClick={() => setRemoving(true)}
              >
                Delete
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Above the notices, because it explains most of what they would say:
          a stale last poll, a sweep that never ran, a write that was refused. */}
      <DisabledBanner
        kind="olt"
        name={olt.name}
        disabledAt={olt.disabledAt}
        disabledReason={olt.disabledReason}
      />

      {notice && (
        <div className="mb-4 rounded-lg border border-info/30 bg-info-soft px-4 py-2.5 text-sm text-info">
          {notice}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mb-4 space-y-2">
          {warnings.map((warning) => (
            <div
              key={warning}
              className="rounded-lg border border-warn/30 bg-warn-soft px-4 py-2.5 text-sm text-warn"
            >
              {warning}
            </div>
          ))}
        </div>
      )}

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-line">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            onClick={() => setTab(entry.id)}
            className={
              tab === entry.id
                ? 'border-b-2 border-primary px-4 py-2.5 text-sm font-medium text-primary'
                : 'border-b-2 border-transparent px-4 py-2.5 text-sm text-muted hover:text-ink'
            }
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'details' && <DetailsTab olt={olt} />}
      {tab === 'cards' && <CardsTab oltId={id} />}
      {tab === 'pon' && <PonTab oltId={id} />}
      {tab === 'uplinks' && <UplinksTab oltId={id} />}
      {tab === 'vlans' && <OltVlansTab oltId={id} oltName={olt.name} />}
      {tab === 'speeds' && <SpeedProfilesTab oltId={id} />}
      {tab === 'onus' && <OnusTab oltId={id} />}
      {tab === 'events' && <EventsTab oltId={id} />}
      {tab === 'console' && (
        <Suspense
          fallback={
            <Card>
              <p className="p-5 text-sm text-faint">Loading the console…</p>
            </Card>
          }
        >
          <ConsoleTab olt={olt} />
        </Suspense>
      )}
      {tab === 'backups' && <BackupsTab olt={olt} />}
      {tab === 'diagnostics' && <DiagnosticsTab oltId={id} vendor={olt.vendor} />}

      {editing && <EditOltDialog olt={olt} onClose={() => setEditing(false)} />}

      {disabling && (
        <DisableDialog
          kind="olt"
          name={olt.name}
          endpoint={`/olts/${encodeURIComponent(id)}/disabled`}
          href={`/olts/${encodeURIComponent(id)}`}
          onClose={() => setDisabling(false)}
        />
      )}

      {linking && (
        <LinkDevicesDialog
          oltId={id}
          oltName={olt.name}
          onClose={() => setLinking(false)}
        />
      )}

      {removing && <DeleteOltDialog olt={olt} onClose={() => setRemoving(false)} />}
    </>
  );
}

/**
 * Deleting a chassis is the one action on this page with nothing on the other
 * side of it — no enable, no restore, no sync that brings it back — so it gets
 * the platform's strongest confirmation: the chassis's own name, typed back.
 * The server is the real guard (it refuses while any ONU, backup or splitter
 * is still attached, naming the counts), and its refusal is shown verbatim
 * rather than paraphrased — the message already says what to remove first.
 *
 * The delete itself runs in the background tray: confirming closes the dialog,
 * leaves the page (it is about to describe a chassis that no longer exists) and
 * the toast reports what was cleared — warnings included, since the dialog that
 * used to hold them is gone by then.
 */
function DeleteOltDialog({ olt, onClose }: { olt: Olt; onClose: () => void }) {
  const navigate = useNavigate();
  const tasks = useTasks();
  const [typed, setTyped] = useState('');

  /**
   * The delete's real footprint, fetched fresh when the dialog opens: the
   * operator confirms "192 ONUs, 41 backups" by name, never an abstract
   * "are you sure". The same read tells us up front if the delete would
   * refuse (a live bulk run), so the refusal is a notice here instead of an
   * error after the name was typed.
   */
  const planQuery = useQuery({
    queryKey: ['olt', olt.id, 'delete-plan'],
    queryFn: () => api.get<OltDeletePlan>(`/olts/${encodeURIComponent(olt.id)}/delete-plan`),
    staleTime: 0,
  });
  const plan = planQuery.data;

  const startDelete = () => {
    const name = olt.name;
    onClose();
    navigate('/olts');
    tasks.start<OltDeleteResult>({
      label: `Delete ${name}`,
      href: '/olts',
      run: () => api.delete<OltDeleteResult>(`/olts/${encodeURIComponent(olt.id)}`),
      done: (r) => {
        const cleared =
          `${name} is gone: ${r.cleared.onus.toLocaleString()} ONUs, ` +
          `${r.cleared.backups.toLocaleString()} backups and ` +
          `${r.cleared.acsDevices.toLocaleString()} ACS devices removed` +
          (r.detachedSplitters > 0
            ? `, ${r.detachedSplitters.toLocaleString()} splitters detached`
            : '');
        return r.warnings.length > 0
          ? `${cleared}. Not cleanly — ${r.warnings.length} warning(s): ${r.warnings.join(' ')}`
          : `${cleared}.`;
      },
      // The rows are gone; every cached list that could still show them is
      // stale. The empty key is TanStack's "match everything" prefix.
      invalidate: [[]],
    });
  };

  const armed = typed.trim().toLowerCase() === olt.name.trim().toLowerCase();

  const line = (n: number, what: string) => (
    <li className="flex items-baseline justify-between gap-4">
      <span>{what}</span>
      <span className="font-mono font-semibold tabular-nums text-ink">{n.toLocaleString()}</span>
    </li>
  );

  return (
    <WizardDialog
      title={`Delete ${olt.name}?`}
      subtitle="Removes this chassis and everything recorded under it from SimpleACS and the ACS. Nothing is sent to the OLT itself — its running config and subscriber service are untouched."
      onClose={onClose}
      footer={
        plan && !planQuery.isLoading && !planQuery.error ? (
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="danger"
              icon={<Trash2 className="size-4" />}
              disabled={!armed || Boolean(plan.blockedBy)}
              onClick={startDelete}
            >
              Delete
            </Button>
          </div>
        ) : undefined
      }
    >
      <>
        {planQuery.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : planQuery.error ? (
          <ErrorNotice error={planQuery.error} />
        ) : plan ? (
          <>
            {plan.blockedBy ? (
              <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                {plan.blockedBy}
              </div>
            ) : null}
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-faint">
                Exactly this goes with it
              </p>
              <ul className="space-y-1.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-muted">
                {line(plan.onus, 'ONUs, with their ports, VLANs and optical history')}
                {line(plan.acsDevices, 'CPE records cleared from the ACS (GenieACS)')}
                {line(plan.backups, 'config backups, including the stored copies')}
                {plan.reports > 0 ? line(plan.reports, 'findings about this chassis') : null}
                {plan.splitters > 0
                  ? line(plan.splitters, 'splitters detached — field housings are never deleted')
                  : null}
              </ul>
            </div>
            <p className="text-sm text-muted">
              Event history, bulk-run history and the action log stay — they carry their own copies
              of the names. A CPE that is still online and pointed at this ACS will register itself
              again on its next inform.
            </p>
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-faint">
                Type the OLT name to confirm
              </label>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={olt.name}
                autoFocus
              />
            </div>
          </>
        ) : null}
      </>
    </WizardDialog>
  );
}

function Row({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line px-5 py-2.5 last:border-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm text-ink">{value ?? '—'}</span>
    </div>
  );
}

function SecretRow({
  label,
  value,
  present,
  fetched,
  canReveal,
  onReveal,
}: {
  label: string;
  value: string | null | undefined;
  present: boolean;
  fetched: boolean;
  canReveal: boolean;
  onReveal: () => void;
}) {
  const [shown, setShown] = useState(false);
  if (!present) return <Row label={label} value="not set" />;
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line px-5 py-2.5 last:border-0">
      <span className="text-sm text-muted">{label}</span>
      <span className="flex items-center gap-2">
        <span className="font-mono text-sm text-ink">
          {shown && fetched ? (value ?? 'not set') : '••••••••••'}
        </span>
        {canReveal && (
          <button
            type="button"
            className="text-faint hover:text-ink"
            title={shown ? 'Hide' : 'Reveal (written to the audit trail)'}
            onClick={() => {
              onReveal();
              setShown((v) => !v);
            }}
          >
            {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        )}
      </span>
    </div>
  );
}

function DetailsTab({ olt }: { olt: Olt }) {
  const { can } = useSession();
  // One audited fetch covers every eye icon; until someone clicks, no secret
  // leaves the server.
  const [revealed, setRevealed] = useState(false);
  const credentials = useQuery({
    queryKey: ['olt', olt.id, 'credentials'],
    queryFn: () => api.get<OltCredentials>(`/olts/${encodeURIComponent(olt.id)}/credentials`),
    enabled: revealed,
    staleTime: 60_000,
  });
  const secretProps = {
    fetched: credentials.isFetched,
    canReveal: can('olt.manage'),
    onReveal: () => setRevealed(true),
  };

  return (
    <div className="space-y-5">
    <div className="grid gap-5 xl:grid-cols-2">
      <Card title="Terminal">
        <Row label="Name" value={olt.name} />
        <Row label="Vendor" value={olt.vendor.toUpperCase()} />
        <Row label="Model" value={olt.model} />
        <Row label="Software" value={olt.softwareVersion} />
        <Row label="Hardware" value={olt.hardwareVersion} />
        <Row label="Uptime" value={formatUptime(olt.uptimeSeconds)} />
        <Row label="Last poll" value={formatRelative(olt.lastPolledAt)} />
        <Row label="Last error" value={olt.lastError} />
      </Card>

      <Card title="Access">
        <Row label="Management IP" value={olt.mgmtIp} />
        <Row label="Reached through" value={olt.siteName ?? 'Directly routable'} />
        <Row label="SNMP version" value={`v${olt.snmpVersion}`} />
        <Row label="SNMP port" value={`udp/${olt.snmpPort}`} />
        <SecretRow
          label="SNMP community (read)"
          value={credentials.data?.snmpCommunity}
          present={olt.snmpVersion !== '3'}
          {...secretProps}
        />
        <SecretRow
          label="SNMP community (read/write)"
          value={credentials.data?.snmpCommunityRw}
          present={olt.snmpVersion !== '3'}
          {...secretProps}
        />
        {olt.snmpVersion === '3' && (
          <SecretRow
            label="SNMPv3 user"
            value={credentials.data?.snmpV3Username}
            present
            {...secretProps}
          />
        )}
        <Row label="CLI transport" value={olt.cliTransport.toUpperCase()} />
        <Row label="CLI port" value={`tcp/${olt.cliPort}`} />
        <Row label="CLI username" value={olt.cliUsername ?? 'not set'} />
        <SecretRow
          label="CLI password"
          value={credentials.data?.cliPassword}
          present={olt.cliUsername !== null}
          {...secretProps}
        />
        <Row label="PON ports" value={olt.ponPortCount} />
        <Row label="ONUs" value={`${olt.onuOnlineCount} online of ${olt.onuCount}`} />
      </Card>
    </div>

      {/* Above Time deliberately: a mismatch means this OLT's sessions are
          being refused right now, and that has to be the first card an
          operator meets after the two it sits under. Stats sits to its left
          because the two answer the same question from opposite ends — is
          this chassis healthy, and can we still reach it. */}
      <div className="grid items-start gap-5 xl:grid-cols-2">
        <OltStatsCard olt={olt} />
        <OltHostKeyCard olt={olt} />
      </div>
      <OltTimeCard olt={olt} />
      <OltProfilesCard olt={olt} />
    </div>
  );
}

/**
 * One line of the Stats card: a label and a figure, and nothing else.
 *
 * The card reads like the panel it replaces — Uptime, Temperature, each fan,
 * Voltage, Power usage — because that is what an operator opens it for. A note
 * under every row turned a six-line readout into three screens of prose, so
 * everything explaining WHERE a figure came from moved behind the Details
 * toggle. It is all still there; it is just not in the way.
 *
 * What never hides: a row with no figure still says so in place. "not
 * available" beats a blank, and beats a zero, whether or not anyone opens
 * Details to find out why.
 */
function StatLine({
  label,
  value,
  detail,
  showDetail,
}: {
  label: string;
  value: string | null;
  detail?: ReactNode;
  showDetail: boolean;
}) {
  return (
    <div className="border-b border-line px-5 py-2.5 last:border-0">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-sm text-muted">{label}</span>
        <span className={value ? 'text-sm font-medium text-ink' : 'text-sm text-faint'}>
          {value ?? 'not available'}
        </span>
      </div>
      {showDetail && detail ? <div className="mt-1.5">{detail}</div> : null}
    </div>
  );
}

/**
 * Where a figure came from: the per-slot readings it was summarised from, and
 * the command that answered.
 *
 * Only rendered under Details. The per-slot list is the part that matters — a
 * chassis reports one temperature per card and the row above shows one per
 * chassis, so a 65 degree card must be reachable from the 39 degree headline.
 * Slots the chassis reports `N/A.` for arrive as "no reading", never as a zero.
 */
function StatDetail({ reading }: { reading: OltStatReading }) {
  const parts = reading.parts ?? [];
  return (
    <>
      {parts.length > 0 && (
        <ul className="space-y-1">
          {parts.map((part) => (
            <li key={part.label} className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-ink">{part.label}</span>
              <span className={part.value === 'no reading' ? 'text-faint' : 'text-muted'}>
                {part.value}
              </span>
            </li>
          ))}
        </ul>
      )}
      {(reading.source || reading.note) && (
        <p className="mt-1 text-xs leading-relaxed text-muted">{reading.note ?? reading.source}</p>
      )}
    </>
  );
}

/**
 * A fan's speed in the unit its own generation answers.
 *
 * RPM on a ZTE C300, a percentage of full speed on a C650, and neither on
 * Huawei — where `display fan speed priority` returns a strategy rather than a
 * reading. Never one derived from the other: there is no ratio between RPM and
 * percent that is not invented.
 */
function fanSpeedText(unit: OltFanUnit): string | null {
  if (unit.rpm !== null) return `${unit.rpm} RPM`;
  if (unit.percent !== null && unit.percent !== undefined) return `${unit.percent}%`;
  return null;
}

/**
 * Uptime, temperature, each fan, voltage and power draw.
 *
 * Uptime is free — SNMP sysUpTime, which every sync already reads. The other
 * figures are a CLI read of the chassis, served from the snapshot the inventory
 * sweep writes, and the header says how old that read is rather than presenting
 * a twenty-minute-old temperature as a live one. Refresh takes a fresh session.
 *
 * Fans are listed one per row, numbered as the CHASSIS numbers them, because
 * renumbering them to start at zero would misreport which unit is which when
 * somebody goes to look at it.
 *
 * Rows with no figure say so. Huawei reports no chassis voltage and no fan
 * speed at all; an MA5683T monitors no environment unit and answers 0 W for
 * one, which is an unmonitored frame rather than a chassis drawing nothing.
 * Details carries the reason for each, and the distinction the card exists to
 * preserve — "checked, this chassis does not report it" versus "nobody looked
 * yet" — is in that text.
 *
 * The poll below costs the chassis nothing: it reads stored rows. Only the
 * Refresh button opens a session.
 */
function OltStatsCard({ olt }: { olt: Olt }) {
  const queryClient = useQueryClient();
  const [showDetail, setShowDetail] = useState(false);
  const query = useQuery({
    queryKey: ['olt', olt.id, 'stats'],
    queryFn: () => api.get<OltStats>(`/olts/${encodeURIComponent(olt.id)}/stats`),
    // Matches the OLT header's own cadence. Uptime is served live from the
    // stored poll, so a frozen card would show a chassis as having rebooted
    // minutes later than it did.
    refetchInterval: 30_000,
  });
  /**
   * The one control on this card that touches the OLT: one SSH session and
   * three to twelve read-only commands. Nothing here writes to a chassis.
   */
  const refresh = useMutation({
    mutationFn: () => api.get<OltStats>(`/olts/${encodeURIComponent(olt.id)}/stats?refresh=1`),
    onSuccess: (fresh) => queryClient.setQueryData(['olt', olt.id, 'stats'], fresh),
  });

  const data = query.data;
  /**
   * One row per fan, but only when the chassis actually answered a speed.
   * Huawei fills this list with the EMU it found and no reading, and six rows
   * of "not available" say less than one Fans row does.
   */
  const fanUnits = data?.fans.value ? data.fans.units : [];

  return (
    <Card
      title="Stats"
      action={
        <div className="flex items-center gap-3">
          {data?.environmentAt ? (
            <span className="text-xs text-faint">
              chassis read {formatRelative(data.environmentAt)}
            </span>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowDetail((on) => !on)}
          >
            {showDetail ? 'Hide details' : 'Details'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw className="size-3.5" />}
            loading={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            Refresh
          </Button>
        </div>
      }
    >
      {/* Above the readings, and outside the query's loading/error branches on
          purpose: the picture of the chassis does not depend on the chassis
          having answered, so a stats read that is still running or has just
          failed leaves the page looking like the OLT it is about rather than
          like an empty card. */}
      <OltImageBlock olt={olt} />
      {query.isPending ? (
        <div className="px-5 py-6">
          <Spinner />
        </div>
      ) : query.error ? (
        <div className="px-5 py-4">
          <ErrorNotice error={query.error} />
        </div>
      ) : data ? (
        <>
          <StatLine
            label="Uptime"
            value={data.uptime.value}
            showDetail={showDetail}
            detail={<StatDetail reading={data.uptime} />}
          />
          <StatLine
            label="Temperature"
            value={data.temperature.value}
            showDetail={showDetail}
            detail={<StatDetail reading={data.temperature} />}
          />
          {fanUnits.length > 0 ? (
            fanUnits.map((unit, index) => (
              <StatLine
                key={unit.label}
                label={unit.label}
                value={fanSpeedText(unit)}
                showDetail={showDetail}
                // The command and the range it summarises belong to the fan
                // ROW, not to each unit, so it is shown once under the first.
                detail={index === 0 ? <StatDetail reading={data.fans} /> : undefined}
              />
            ))
          ) : (
            <StatLine
              label="Fans"
              value={data.fans.value}
              showDetail={showDetail}
              detail={<StatDetail reading={data.fans} />}
            />
          )}
          <StatLine
            label="Voltage"
            value={data.voltage.value}
            showDetail={showDetail}
            detail={<StatDetail reading={data.voltage} />}
          />
          <StatLine
            label="Power usage"
            value={data.power.value}
            showDetail={showDetail}
            detail={<StatDetail reading={data.power} />}
          />
          {showDetail && data.fanEvidence.length > 0 && (
            <div className="px-5 py-2.5">
              {/* The lines the fan rows were read from. An operator should be
                  able to check a claim about their chassis without opening a
                  2.5 MB config. */}
              <p className="text-xs text-muted">
                From {data.configSource}
                {data.configTakenAt ? `, ${formatRelative(data.configTakenAt)}` : ''}:
              </p>
              <pre className="mt-1 overflow-x-auto whitespace-pre-wrap font-mono text-xs text-muted">
                {data.fanEvidence.join('\n')}
              </pre>
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
}

/** How far out, in the largest unit that still reads as a number. */
function driftLabel(seconds: number): string {
  const magnitude = Math.abs(seconds);
  const sign = seconds > 0 ? '+' : '−';
  if (magnitude >= 3600) return `${sign}${Math.round((magnitude / 3600) * 10) / 10}h`;
  if (magnitude >= 60) return `${sign}${Math.round(magnitude / 60)}m`;
  return `${sign}${magnitude}s`;
}

interface OltTime {
  clock: {
    /** What the chassis printed, verbatim. */
    reported: string | null;
    at: number | null;
    timezone: string | null;
    /** null = the chassis does not say, which is not the same as unsynchronised. */
    ntpSynchronised: boolean | null;
    ntpServer: string | null;
    ntpStratum: number | null;
  } | null;
  ntp: {
    servers: string[];
    interval: number | null;
    enabled: boolean | null;
    /**
     * Whether `servers` is an ANSWER.
     *
     * false means the chassis would not say, and the empty list means nothing.
     * A ZTE C300 does not answer `show running-config ntp` — that is a C650
     * command — and reading its refusal as "no servers configured" is what had
     * a correctly configured, correctly synchronised chassis reported as
     * having no time source at all. Optional so an older API keeps parsing.
     */
    serversKnown?: boolean;
    unknownReason?: string | null;
    /** The upstream clock the chassis says it is actually following. */
    reference?: string | null;
  } | null;
  drift: { seconds: number | null; severity: 'ok' | 'warn' | 'bad'; note: string } | null;
  /** The zone SimpleACS holds, and whether the chassis was ever told it. */
  timezone?: { held: string | null; appliedAt: number | null; label: string | null } | null;
  /**
   * Whether SimpleACS set this clock by hand, and when.
   *
   * The field this card exists to be honest about. A hand-set clock is right
   * for one instant and drifts from then on — it is NOT synchronised, and a
   * chassis following a stratum-2 server and a chassis SimpleACS poked at
   * 14:32 print identical `show clock` output. Without this the card could
   * only say "no — the clock is free-running" about a clock it had itself just
   * corrected, or, far worse, be tempted to call it synchronised.
   */
  clockSet?: {
    at: number | null;
    /** How far out it was when it was corrected — the size of the jump. */
    driftSeconds: number | null;
    zone: string | null;
    source: 'operator' | 'auto' | null;
    by: string | null;
  } | null;
  /**
   * Whether the chassis is actually reaching the servers it holds, and how far
   * the automatic fallback has got towards deciding it is not.
   *
   * `verdict` is deliberately five-valued. "We could not read it" is a
   * different answer from "it has no servers" and from "it has servers and
   * none of them answers", and this card has already been wrong once by
   * collapsing the first into the second.
   */
  reach?: {
    verdict: 'synchronised' | 'unreachable' | 'reaching' | 'no-servers' | 'unknown';
    reason: string;
    reachReported: number;
    reachZero: number;
    unreachableSince: number | null;
    unreachableChecks: number;
    checksNeeded: number;
  } | null;
  checkedAt: number;
}

/** The command SimpleACS would send to set this chassis's clock. */
interface ClockSetPlanDto {
  family: string;
  commands: string[];
  wall: string | null;
  zone: string | null;
  zoneSource: 'utc' | 'chassis' | 'platform' | null;
  offsetMinutes: number | null;
  driftSeconds: number | null;
  refusal: string | null;
  note: string;
}

/**
 * The chassis clock, and the time servers — both what SimpleACS holds and what
 * the chassis is actually configured with.
 *
 * Side by side deliberately. They disagree in ways that matter and that neither
 * reading shows alone: a chassis listing three servers and still sitting at
 * stratum 16 has a reachability problem, not a configuration one, and a chassis
 * with a right clock and no servers at all is correct today and drifting
 * quietly. The C650 was found in the first state.
 */
/** One entry of the platform's list, and the address the chassis was sent. */
type NtpResolutionRow = {
  server: string;
  address: string;
  resolved: boolean;
  alreadyOnChassis: boolean;
};

/**
 * Is this an address, or a name?
 *
 * Deliberately crude, and only ever used to decide whether a comparison is
 * MEANINGFUL — never to validate anything. The API resolves and validates for
 * real; all this has to answer is "can the browser compare this against the
 * chassis's list without lying", and for a name the answer is no.
 */
function isIpLiteral(value: string): boolean {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(value) || value.includes(':');
}

function OltTimeCard({ olt }: { olt: Olt }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [resolutions, setResolutions] = useState<NtpResolutionRow[]>([]);
  const [platformTimeOpen, setPlatformTimeOpen] = useState(false);

  const time = useQuery({
    queryKey: ['olt', olt.id, 'time'],
    queryFn: () => api.get<OltTime>(`/olts/${encodeURIComponent(olt.id)}/time`),
  });

  /**
   * Sync makes the chassis match the platform — including by removing.
   *
   * The response says which of the two things happened, because they are
   * genuinely different: a chassis that listed its servers had them replaced,
   * and one that would not (a C300 refuses the listing command) kept what it
   * had and took ours alongside. Guessing at what to delete on a chassis that
   * will not say what it has is not something to do to free a priority.
   */
  const sync = useMutation({
    mutationFn: () =>
      api.post<{
        servers: string[];
        removed?: string[];
        warnings?: string[];
        resolved?: NtpResolutionRow[];
      }>(`/olts/${encodeURIComponent(olt.id)}/time/ntp/sync`, {}),
    onSuccess: async (result) => {
      const removed = result.removed ?? [];
      // Which address each NAME was written as. Neither vendor takes a
      // hostname, so `servers` above is a list of numbers, and without this an
      // operator has no way to tell which of their entries each number came
      // from. Only names are worth reporting: an address that was typed in
      // travelled unchanged and saying so is noise.
      setResolutions((result.resolved ?? []).filter((r) => r.resolved));
      // "Nothing was written" is an outcome worth saying out loud rather than
      // dressing up as a successful push: a chassis that already carries every
      // server the platform holds is left completely alone, and an operator who
      // is told it was "now pointed at" them has no way to tell that apart from
      // a run that rewrote its whole list.
      const untouched = result.warnings?.some((w) => /nothing was written/i.test(w));
      setNotice(
        removed.length > 0
          ? `${olt.name} now asks ${result.servers.join(', ')}. ${removed.join(', ')} ${removed.length === 1 ? 'was' : 'were'} removed — a sync makes the chassis match the platform.`
          : untouched
            ? `${olt.name} already carries the time servers SimpleACS holds, so nothing was written.`
            : `${olt.name} is now pointed at the time servers SimpleACS holds.${
                result.warnings?.some((w) => /not list|would not list/i.test(w))
                  ? ' It would not list what it already has, so nothing was removed.'
                  : ''
              }`,
      );
      await queryClient.invalidateQueries({ queryKey: ['olt', olt.id, 'time'] });
    },
    onError: (err) => setNotice(err instanceof Error ? err.message : 'Could not set the servers'),
  });

  const held = olt.ntpServers ?? [];
  const onChassis = time.data?.ntp?.servers ?? [];
  /**
   * Entries of the platform's list that are NAMES rather than addresses.
   *
   * This matters for the comparison below, not only for the copy. Neither
   * vendor takes a hostname — both were probed, both answer their `?` help
   * with address forms only — so `time.google.com` is resolved on the API
   * server and the chassis is sent something like `216.239.35.4`. Comparing
   * the name against the chassis's list as text can only ever say "missing",
   * for a chassis that is asking exactly the right server. That sentence would
   * be permanent, and it would be wrong every time.
   */
  const heldNames = held.filter((h) => !isIpLiteral(h));
  /**
   * Did the chassis actually list its servers?
   *
   * Older API responses have no such field and did list them, so an absent
   * flag means yes. A present `false` means the chassis refused the read —
   * a C300 answers `show running-config ntp` with `%Error 20201: Invalid
   * input` — and the empty list below is a gap, not an answer.
   */
  const serversKnown = time.data?.ntp ? (time.data.ntp.serversKnown ?? true) : false;
  // Only worth saying once both are known — "missing" against an unread chassis
  // is a statement about the read, not about the OLT, and saying every server
  // is missing on a chassis that simply would not answer is how an operator
  // ends up re-pushing a configuration that was already correct.
  // Addresses only. A name cannot be compared here — the browser has no
  // resolver and the answer changes between lookups anyway — so a name is
  // reported as what it is, once, below, instead of being called missing.
  const missing = serversKnown
    ? held.filter((h) => isIpLiteral(h) && !onChassis.includes(h))
    : [];
  /**
   * The other direction: what a sync would take OFF this chassis.
   *
   * Suppressed entirely while the platform's list holds a name, because an
   * address on the chassis may well BE that name — telling an operator their
   * working time server is about to be removed, when a sync would recognise it
   * and leave the chassis alone, is the worst way to be wrong about this.
   */
  const extra =
    serversKnown && heldNames.length === 0
      ? onChassis.filter((c) => !held.includes(c))
      : [];
  const following = time.data?.clock?.ntpServer ?? time.data?.ntp?.reference ?? null;
  const zone = time.data?.timezone ?? null;
  const clockSet = time.data?.clockSet ?? null;
  const reach = time.data?.reach ?? null;
  const synchronised = time.data?.clock?.ntpSynchronised === true;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Clock className="size-4 text-muted" />
          Time
          {time.data?.drift && time.data.drift.seconds !== null && (
            <Pill state
              tone={
                time.data.drift.severity === 'ok'
                  ? 'success'
                  : time.data.drift.severity === 'warn'
                    ? 'warn'
                    : 'danger'
              }
            >
              {time.data.drift.severity === 'ok' ? 'in step' : driftLabel(time.data.drift.seconds)}
            </Pill>
          )}
        </span>
      }
      action={
        // Offered even when this OLT holds none of its own: the platform keeps
        // a set (Settings → NTP servers) and the endpoint falls back to it, so
        // hiding the button would hide the answer. With neither set, the API
        // says so and names where to put them.
        can('olt.manage') ? (
          <span className="flex items-center gap-2">
            {/* A chassis whose management path reaches no public time server
                (Lagos's C650, 2026-10-06) keeps time from the platform's own. */}
            <Button
              size="sm"
              variant="ghost"
              icon={<Clock className="size-3.5" />}
              onClick={() => setPlatformTimeOpen(true)}
              title="Ask the platform's own server the time, over the management path — with a route through the site router where the chassis cannot reach it now."
            >
              Platform time
            </Button>
            <Button
              size="sm"
              icon={<RefreshCw className="size-3.5" />}
              loading={sync.isPending}
              onClick={() => sync.mutate()}
              title="Make the chassis ask the time servers SimpleACS holds, and only those. Servers it has that the platform does not are removed — which is what frees the ZTE priorities. A chassis that will not list what it holds keeps it, and the platform's servers are added alongside. The platform's own server, where the chassis asks it over management, is never removed."
            >
              Sync NTP
            </Button>
          </span>
        ) : null
      }
    >
      {platformTimeOpen && (
        <ManagementNtpDialog oltId={olt.id} oltName={olt.name} onClose={() => setPlatformTimeOpen(false)} />
      )}
      {notice && <p className="border-b border-line px-5 py-2.5 text-sm text-muted">{notice}</p>}
      {/* What each NAME was actually written as, after a sync. `alreadyOnChassis`
          is the interesting case: it means the address picked was one the
          chassis already had, so nothing moved — which is the difference
          between a quiet run and a chassis whose time source was rebuilt. */}
      {resolutions.length > 0 && (
        <ul className="border-b border-line px-5 py-2.5">
          {resolutions.map((r) => (
            <li key={r.server} className="text-sm text-muted">
              {r.server} &rarr; <span className="font-mono">{r.address}</span>
              {r.alreadyOnChassis ? ' — already on the chassis, so nothing moved' : ''}
            </li>
          ))}
        </ul>
      )}
      {time.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <>
          <Row label="Chassis clock" value={time.data?.clock?.reported ?? 'not read'} />
          {/* Three different answers, and the third one is the whole reason
              this row was rewritten. A chassis SimpleACS set by hand is NOT
              synchronised — it was right for one instant and has been drifting
              since, with nothing keeping it honest — and calling it
              synchronised would be the most useful-sounding lie this card
              could tell. It says which it is, and when. */}
          <Row
            label="Synchronised"
            value={
              time.data?.clock?.ntpSynchronised === null ||
              time.data?.clock?.ntpSynchronised === undefined
                ? 'the chassis does not say'
                : synchronised
                  ? `yes${following ? `, following ${following}` : ''}${
                      time.data.clock.ntpStratum ? ` (stratum ${time.data.clock.ntpStratum})` : ''
                    }`
                  : clockSet?.at
                    ? `no — SimpleACS set this clock ${formatRelative(clockSet.at)} and it has no time source. Free-running since.`
                    : 'no — the clock is free-running'
            }
          />
          {/* Only when it happened. A clock set is a JUMP and not a slew, so
              the chassis's own log is discontinuous across it with nothing in
              it saying why; this row and the audit entry behind it are the
              explanation, kept where somebody reading that log a year later
              can find it. */}
          {clockSet?.at && (
            <Row
              label="Clock set by SimpleACS"
              value={`${formatRelative(clockSet.at)}${
                clockSet.driftSeconds === null
                  ? ''
                  : `, correcting ${driftLabel(clockSet.driftSeconds)}`
              }${clockSet.zone ? ` · rendered in ${clockSet.zone}` : ''}${
                clockSet.source === 'auto'
                  ? ' · automatic fallback'
                  : clockSet.by
                    ? ` · ${clockSet.by}`
                    : ''
              }`}
            />
          )}
          <Row
            label="Servers on the chassis"
            value={
              !serversKnown
                ? // Never "none configured". That sentence, printed for a
                  // chassis that had refused the question, is the whole bug
                  // this card was reporting for months.
                  `not readable on this chassis${following ? ` — it is following ${following}` : ''}`
                : onChassis.length > 0
                  ? onChassis.join(', ')
                  : 'none configured'
            }
          />
          <Row
            label="Servers SimpleACS holds"
            value={
              held.length > 0
                ? held.join(', ')
                : 'none of its own — Sync NTP writes the platform set from Settings → NTP servers'
            }
          />
          {/* The chassis's own words about the drift, which say why it matters
              rather than only how large it is. */}
          {time.data?.drift && time.data.drift.seconds !== null && time.data.drift.severity !== 'ok' && (
            <p
              className={`border-t border-line px-5 py-2.5 text-sm ${
                time.data.drift.severity === 'bad' ? 'text-danger' : 'text-warn'
              }`}
            >
              {time.data.drift.note}
            </p>
          )}
          {/* Configured, and reaching nothing. This is the state the operator's
              C650 is in — four servers listed, stratum 16, `Reach: 0` on every
              association — and it is invisible in a configuration listing,
              which is exactly why it went unnoticed. The count is shown while
              it accumulates so that the fallback is something an operator can
              see coming rather than find in a log afterwards. */}
          {reach?.verdict === 'unreachable' && (
            <p className="border-t border-line px-5 py-2.5 text-sm text-warn">
              {reach.reason}
              {reach.unreachableChecks > 0 && (
                <>
                  {' '}
                  Confirmed on {reach.unreachableChecks} of {reach.checksNeeded} consecutive checks
                  {reach.unreachableSince ? `, since ${formatRelative(reach.unreachableSince)}` : ''}
                  . SimpleACS sets the clock by hand once that holds — the servers stay configured,
                  and it goes back to following them the moment the path to them works again.
                </>
              )}
            </p>
          )}
          {/* Not a warning, and deliberately said out loud: something IS
              answering, so the chassis's own NTP is about to move this clock
              and a hand-set value would be undone. */}
          {reach?.verdict === 'reaching' && (
            <p className="border-t border-line px-5 py-2.5 text-sm text-muted">{reach.reason}</p>
          )}
          <Row
            label="Timezone"
            value={
              !zone?.held
                ? // Worth saying what is lost by leaving it unset: the OLT's
                  // schedules fall back to the server's clock, which is the
                  // wrong one the moment the chassis is in another country.
                  "none held — set one with Edit; this OLT's schedules use the server's clock until then"
                : zone.appliedAt
                  ? `${zone.label ?? zone.held} · schedules run in it · written to the chassis ${formatRelative(zone.appliedAt)}`
                  : `${zone.label ?? zone.held} · schedules run in it · never written to the chassis`
            }
          />
          {/* Why the chassis would not list its servers, in its own terms. Not
              a warning — a chassis that refuses a C650 command is behaving
              correctly, and what is wrong is only what we can see. */}
          {time.data?.ntp && !serversKnown && time.data.ntp.unknownReason && (
            <p className="border-t border-line px-5 py-2.5 text-sm text-muted">
              {time.data.ntp.unknownReason}
            </p>
          )}
          {/* Not a warning: holding a name is fine and is the friendly thing to
              hold. It just cannot be checked against the chassis from here, and
              an operator comparing the two rows above by eye deserves to know
              why they will never match. */}
          {heldNames.length > 0 && (
            <p className="border-t border-line px-5 py-2.5 text-sm text-muted">
              {heldNames.join(', ')} {heldNames.length === 1 ? 'is a name' : 'are names'}. Neither
              vendor's chassis takes one, so Sync NTP resolves{' '}
              {heldNames.length === 1 ? 'it' : 'them'} here and writes the address — which is why
              the two lists above will not read the same. Sync NTP reports which address it used,
              and leaves the chassis alone when it already holds one of them.
            </p>
          )}
          {missing.length > 0 && (
            <p className="border-t border-line px-5 py-2.5 text-sm text-warn">
              {missing.join(', ')} {missing.length === 1 ? 'is' : 'are'} recorded here but not on the
              chassis. Sync NTP writes {missing.length === 1 ? 'it' : 'them'}.
            </p>
          )}
          {/* And the other direction, which Sync now acts on: a server the
              chassis asks that the platform does not list comes OFF. Said
              before the button is pressed rather than reported after — only
              when SimpleACS holds a list of its own for this OLT, because with
              none held the platform's set is used and this card cannot see it. */}
          {held.length > 0 && extra.length > 0 && (
            <p className="border-t border-line px-5 py-2.5 text-sm text-warn">
              {extra.join(', ')} {extra.length === 1 ? 'is' : 'are'} on the chassis and not in this
              OLT's list.{' '}
              {missing.length > 0
                ? `Sync NTP removes ${extra.length === 1 ? 'it' : 'them'} — a sync makes the chassis match the platform.`
                : // Nothing this OLT holds is missing, so a sync has nothing to
                  // place and no rank to reclaim, and it writes nothing at all.
                  // Saying "Sync NTP removes them" here would be a promise the
                  // button does not keep — and keeping it would mean taking a
                  // working time source off a live chassis for no change.
                  `Sync NTP leaves ${extra.length === 1 ? 'it' : 'them'} alone: every server this OLT holds is already on the chassis, so nothing has to move to make room.`}
            </p>
          )}
          {zone?.held && !zone.appliedAt && can('olt.manage') && (
            <TimezonePush olt={olt} zone={zone.held} onDone={() => time.refetch()} />
          )}
          {can('olt.manage') && (
            <ClockSetPanel
              olt={olt}
              synchronised={synchronised}
              following={following}
              onDone={() => {
                void queryClient.invalidateQueries({ queryKey: ['olt', olt.id, 'time'] });
              }}
            />
          )}
        </>
      )}
    </Card>
  );
}

interface TimezonePlanDto {
  zone: string;
  offsetMinutes: number;
  commands: string[];
  evidence: 'captured' | 'running-config' | 'keyword-only';
  verified: boolean;
  note: string;
  confirmOnHardware: string;
}

/**
 * Putting a chassis in a timezone.
 *
 * The ceremony here is per VENDOR, and it follows the evidence rather than a
 * list kept in the page: `plan.verified` decides. ZTE's form was read off a
 * live C300 (`clock timezone <name> <signed hours> [minutes]`), so a ZTE
 * chassis takes it like any other write. Huawei's has not been — its running
 * config PRINTS `timezone GMT+ 03:00`, which is not proof that is how it is
 * entered — so the command is shown, the evidence is shown, the probe that
 * would settle it is shown, and the operator has to tick a box before Apply
 * does anything.
 *
 * Not theatre. Every up-time, down-time, down-cause and optical reading a
 * chassis reports is stamped with its own clock: a timezone an hour out puts a
 * subscriber's outage in the wrong part of the day on every OLT that took it,
 * it does not look like a fault, and nothing anywhere complains.
 */
function TimezonePush({ olt, zone, onDone }: { olt: Olt; zone: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plan = useQuery({
    queryKey: ['olt', olt.id, 'timezone-plan', zone],
    queryFn: () =>
      api.post<CliActionResult & { timezone: TimezonePlanDto }>(
        `/olts/${encodeURIComponent(olt.id)}/time/timezone`,
        { zone, dryRun: true },
      ),
    enabled: open,
  });

  const verified = plan.data?.timezone.verified ?? false;

  const apply = useMutation({
    mutationFn: () =>
      api.post(`/olts/${encodeURIComponent(olt.id)}/time/timezone`, {
        zone,
        dryRun: false,
        // Sent only when there is something to confirm. On a vendor whose form
        // was captured there is no unverified command to accept, and a client
        // that ticks the box anyway would carry the acceptance forward to the
        // day some other vendor's form is still a guess.
        confirmUnverified: !verified,
      }),
    onSuccess: () => {
      setOpen(false);
      setAccepted(false);
      onDone();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'The chassis refused it'),
  });


  return (
    <div className="border-t border-line px-5 py-3">
      {!open ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-muted">
            SimpleACS holds {zone} for this OLT but has never written it to the chassis.
          </span>
          <Button size="sm" onClick={() => setOpen(true)}>
            Write timezone…
          </Button>
        </div>
      ) : plan.isPending ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : plan.error ? (
        <ErrorNotice error={plan.error} />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted">{plan.data?.timezone.note}</p>
          {!verified && (
            <>
              <p className="text-sm text-warn">{plan.data?.timezone.confirmOnHardware}</p>
              <label className="flex items-start gap-2 text-sm text-muted">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                I have checked this command form on a chassis of this vendor, and accept that a
                wrong one shifts every timestamp {olt.name} reports.
              </label>
            </>
          )}
          {error && <ErrorNotice error={new Error(error)} />}
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              onClick={() => {
                setOpen(false);
                setAccepted(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!verified && !accepted}
              loading={apply.isPending}
              onClick={() => apply.mutate()}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Setting the chassis clock by hand.
 *
 * The fallback for a chassis that has time servers configured and cannot reach
 * any of them — the operator's C650, which lists four and still reports
 * `Clock is unsynchronized, stratum 16, no reference clock` with `Reach: 0` on
 * every association. Re-pushing the same four addresses cannot fix a
 * management path that will not carry UDP/123.
 *
 * Dry-run first, like every other chassis write on this page, and here the dry
 * run is worth more than usual: the command is three different commands and
 * seeing which one this chassis is about to be sent is most of the review.
 *
 *   C300     clock set 14:32:05 aug 27 2026            its local wall time
 *   C650     clock set utc-time 12:00:00 08-27-2026    UTC — no zone involved
 *   Huawei   time 2026-08-27 14:32:05                  its local wall time
 *
 * There is no "I accept an unverified command form" box, unlike the timezone
 * push above it: all three grammars were typed at live hardware. The checkbox
 * that IS here guards the opposite risk — a chassis that is synchronised is
 * following something better than a hand-set value, and taking it off that
 * needs to be a deliberate act.
 */
function ClockSetPanel({
  olt,
  synchronised,
  following,
  onDone,
}: {
  olt: Olt;
  synchronised: boolean;
  following: string | null;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const plan = useQuery({
    queryKey: ['olt', olt.id, 'clock-plan'],
    queryFn: () =>
      api.post<CliActionResult & { plan: ClockSetPlanDto }>(
        `/olts/${encodeURIComponent(olt.id)}/time/clock`,
        { dryRun: true },
      ),
    enabled: open,
    // The command is a timestamp. A plan cached from five minutes ago would
    // show — and then send — the wrong second.
    gcTime: 0,
    staleTime: 0,
  });

  const apply = useMutation({
    mutationFn: () =>
      api.post(`/olts/${encodeURIComponent(olt.id)}/time/clock`, {
        dryRun: false,
        // Only ever sent for the case it exists for. A client that always sent
        // it would carry the acceptance forward to a chassis that had come
        // back into sync between the dry run and the apply.
        force: synchronised,
      }),
    onSuccess: () => {
      setOpen(false);
      setAccepted(false);
      onDone();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'The chassis refused it'),
  });

  return (
    <div className="border-t border-line px-5 py-3">
      {!open ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-muted">
            {synchronised
              ? `${olt.name} is following a time source, so its clock is already being kept honest.`
              : `Set ${olt.name}'s clock from this server — a fallback for a chassis that cannot reach its time servers.`}
          </span>
          <Button size="sm" onClick={() => setOpen(true)}>
            Set clock now…
          </Button>
        </div>
      ) : plan.isPending ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : plan.error ? (
        <ErrorNotice error={plan.error} />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted">{plan.data?.plan.note}</p>
          {/* The chassis's own words about what this costs — how far it jumps,
              and that a jump leaves its log discontinuous. */}
          {plan.data?.warnings.map((w) => (
            <p key={w} className="text-sm text-muted">
              {w}
            </p>
          ))}
          {synchronised && (
            <label className="flex items-start gap-2 text-sm text-warn">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
              />
              {olt.name} is synchronised{following ? ` and following ${following}` : ''}. I want to
              take it off that and set the clock by hand — and I accept that a hand-set clock has
              nothing keeping it honest, and that the next poll may pull it back anyway.
            </label>
          )}
          {error && <ErrorNotice error={new Error(error)} />}
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              onClick={() => {
                setOpen(false);
                setAccepted(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={synchronised && !accepted}
              loading={apply.isPending}
              onClick={() => apply.mutate()}
            >
              Apply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Which TR-069 profiles this OLT's ONUs may use, and which one they default to.
 * Leaving it empty falls back to the org's local GenieACS — so a single-ACS
 * operator never has to touch this. An ONU can still be pointed at any profile
 * in this set individually from its own page.
 */
function OltProfilesCard({ olt }: { olt: Olt }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const canManage = can('olt.manage');
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const assocQuery = useQuery({
    queryKey: ['olt', olt.id, 'tr069-profiles'],
    queryFn: () => api.get<{ items: OltTr069Profile[] }>(`/olts/${encodeURIComponent(olt.id)}/tr069-profiles`),
  });
  // The org's full catalogue to choose from — scoped by the OLT's org so it
  // works for a superadmin who has no acting org selected.
  const catalogueQuery = useQuery({
    queryKey: ['tr069-profiles', olt.orgId],
    queryFn: () => api.get<{ items: Tr069Profile[] }>('/tr069-profiles', { orgId: olt.orgId }),
    enabled: editing,
  });

  const associated = assocQuery.data?.items ?? [];
  const catalogue = catalogueQuery.data?.items ?? [];

  function beginEdit() {
    setSelected(new Set(associated.map((a) => a.profileId)));
    setDefaultId(associated.find((a) => a.isDefault)?.profileId ?? null);
    setError(null);
    setEditing(true);
  }

  const save = useMutation({
    mutationFn: () =>
      api.put<{ items: OltTr069Profile[] }>(`/olts/${encodeURIComponent(olt.id)}/tr069-profiles`, {
        profileIds: [...selected],
        defaultProfileId: defaultId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['olt', olt.id, 'tr069-profiles'] });
      setEditing(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not save'),
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (defaultId === id) setDefaultId(null);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Cloud className="size-4 text-muted" />
          TR-069 profiles
        </span>
      }
      action={
        canManage &&
        (editing ? (
          <div className="flex gap-2">
            <Button variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            <Button size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button size="sm" icon={<Pencil className="size-3.5" />} onClick={beginEdit}>
            Edit
          </Button>
        ))
      }
    >
      {assocQuery.isLoading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : editing ? (
        <div className="space-y-3 p-5">
          {catalogueQuery.isLoading ? (
            <div className="flex justify-center p-4">
              <Spinner />
            </div>
          ) : (
            <>
              <p className="text-xs text-muted">
                Tick the profiles this OLT may use, and choose which one new ONUs default to. None
                ticked falls back to the org's local GenieACS.
              </p>
              <div className="divide-y divide-line rounded-lg border border-line">
                {catalogue.map((profile) => {
                  const checked = selected.has(profile.id);
                  return (
                    <div key={profile.id} className="flex items-center gap-3 px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(profile.id)}
                      />
                      <span className="flex flex-1 items-center gap-2">
                        {profile.kind === 'local' ? (
                          <Server className="size-3.5 text-muted" />
                        ) : (
                          <Cloud className="size-3.5 text-muted" />
                        )}
                        <span className="text-sm text-ink">{profile.name}</span>
                        <span className="font-mono text-[11px] text-faint">{profile.acsUrl}</span>
                      </span>
                      <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted">
                        <input
                          type="radio"
                          name="olt-default-profile"
                          disabled={!checked}
                          checked={defaultId === profile.id}
                          onChange={() => setDefaultId(profile.id)}
                        />
                        default
                      </label>
                    </div>
                  );
                })}
              </div>
              {error && <ErrorNotice error={new Error(error)} />}
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 p-5">
          {associated.map((a) => (
            <Pill key={a.profileId} tone={a.isDefault ? 'primary' : 'neutral'}>
              <span className="flex items-center gap-1">
                {a.kind === 'local' ? <Server className="size-3" /> : <Cloud className="size-3" />}
                {a.name}
                {a.isDefault && <span className="text-[10px] uppercase tracking-wide">· default</span>}
              </span>
            </Pill>
          ))}
          {associated.length === 1 && associated[0]?.kind === 'local' && (
            <span className="self-center text-[11px] text-faint">
              this OLT's ONUs use the org's local GenieACS
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

// --- edit ---------------------------------------------------------------------

/**
 * Editing an OLT records things. It sends nothing to the chassis.
 *
 * It used to carry the time servers and a ticked-by-default "write them to the
 * chassis on save", and that is how renaming an OLT produced:
 *
 *   Saved, but gachie1 did not take the time servers: …
 *
 * — a half-succeeded save, reporting a failure in a setting the operator was
 * not editing, on a chassis that was already correctly synchronised. Time
 * servers are the platform's now (Settings → NTP servers) and reach a chassis
 * only through Sync on the Time card, where removing and rewriting a chassis's
 * list is the deliberate act it should be.
 *
 * The timezone took their place, and it is not decoration: it is the clock
 * this OLT's nightly backup and drift check are scheduled in. A chassis in
 * Accra backs up at 02:00 Accra time even when the server is in Nairobi, and
 * this field is the only thing that says so.
 */
function EditOltDialog({ olt, onClose }: { olt: Olt; onClose: () => void }) {
  const tasks = useTasks();
  const [form, setForm] = useState({
    name: olt.name,
    mgmtIp: olt.mgmtIp,
    snmpVersion: olt.snmpVersion,
    snmpPort: String(olt.snmpPort),
    snmpCommunity: '',
    snmpCommunityRw: '',
    cliTransport: olt.cliTransport,
    cliPort: String(olt.cliPort),
    cliUsername: olt.cliUsername ?? '',
    cliPassword: '',
    timezone: olt.timezone ?? null,
    // Through the helper, never `olt.eventNotifications` directly: an OLT
    // registered before this existed sends nothing here, and absent means ON.
    eventNotifications: eventNotificationsOn(olt),
  });

  // Save CLOSES the dialog and hands the write to the tray. The body is
  // snapshotted first because the form state unmounts with the dialog.
  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const body = {
      name: form.name.trim(),
      mgmtIp: form.mgmtIp.trim(),
      snmpVersion: form.snmpVersion,
      snmpPort: Number(form.snmpPort),
      cliTransport: form.cliTransport,
      cliPort: Number(form.cliPort),
      cliUsername: form.cliUsername.trim() || undefined,
      // Blank means "keep" — secrets are never echoed back into the form.
      ...(form.snmpCommunity.trim() ? { snmpCommunity: form.snmpCommunity.trim() } : {}),
      ...(form.snmpCommunityRw.trim() ? { snmpCommunityRw: form.snmpCommunityRw.trim() } : {}),
      ...(form.cliPassword ? { cliPassword: form.cliPassword } : {}),
      // Nothing about time servers is in this body, deliberately: a field
      // this form does not show must not be cleared by this form saving.
      // Whatever an OLT holds of its own survives an edit untouched.
      timezone: form.timezone,
      eventNotifications: form.eventNotifications,
    };
    const name = body.name || olt.name;
    onClose();
    tasks.start({
      label: `Save ${name}`,
      run: () => api.patch(`/olts/${encodeURIComponent(olt.id)}`, body),
      done: () => `${name} saved.`,
      /**
       * The OLT LIST has to be re-read too, not just this OLT.
       *
       * Silencing a chassis changes what the list draws — its Events cell and
       * the triangle after its name — and that page is served from a different
       * query key. Without this, an operator who turns notifications off and
       * navigates back finds the chassis still nagging until the list's own
       * poll comes round.
       */
      invalidate: [['olt', olt.id], ['olts']],
    });
  }

  return (
    <WizardDialog
      onClose={onClose}
      title={`Edit ${olt.name}`}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="edit-olt-form" variant="primary">
            Save
          </Button>
        </div>
      }
    >
      <form id="edit-olt-form" onSubmit={onSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </Field>
          <Field label="Management IP">
            <Input
              value={form.mgmtIp}
              onChange={(e) => setForm({ ...form, mgmtIp: e.target.value })}
              className="font-mono"
              required
            />
          </Field>
          <Field label="SNMP version">
            <select
              className="input"
              value={form.snmpVersion}
              onChange={(e) => setForm({ ...form, snmpVersion: e.target.value as Olt['snmpVersion'] })}
            >
              <option value="1">v1</option>
              <option value="2c">v2c</option>
              <option value="3">v3</option>
            </select>
          </Field>
          <Field label="SNMP port">
            <Input
              type="number"
              min={1}
              max={65535}
              value={form.snmpPort}
              onChange={(e) => setForm({ ...form, snmpPort: e.target.value })}
            />
          </Field>
          <Field label="SNMP community (read)" hint="Leave blank to keep the current one">
            <Input
              value={form.snmpCommunity}
              onChange={(e) => setForm({ ...form, snmpCommunity: e.target.value })}
              placeholder="unchanged"
              autoComplete="off"
            />
          </Field>
          <Field
            label="SNMP community (read/write)"
            hint="From `show snmp config` on the OLT — used only where a write is needed"
          >
            <Input
              value={form.snmpCommunityRw}
              onChange={(e) => setForm({ ...form, snmpCommunityRw: e.target.value })}
              placeholder="unchanged"
              autoComplete="off"
            />
          </Field>
          <Field label="CLI transport">
            <select
              className="input"
              value={form.cliTransport}
              onChange={(e) =>
                setForm({ ...form, cliTransport: e.target.value as Olt['cliTransport'] })
              }
            >
              <option value="ssh">SSH</option>
              <option value="telnet">Telnet</option>
            </select>
          </Field>
          <Field label="CLI port">
            <Input
              type="number"
              min={1}
              max={65535}
              value={form.cliPort}
              onChange={(e) => setForm({ ...form, cliPort: e.target.value })}
            />
          </Field>
          <Field label="CLI username">
            <Input
              value={form.cliUsername}
              onChange={(e) => setForm({ ...form, cliUsername: e.target.value })}
              autoComplete="off"
            />
          </Field>
          <Field label="CLI password" hint="Leave blank to keep the current one">
            <Input
              type="password"
              value={form.cliPassword}
              onChange={(e) => setForm({ ...form, cliPassword: e.target.value })}
              placeholder="unchanged"
              autoComplete="new-password"
            />
          </Field>
          {/* The zone. There is no "write it to the chassis on save" beside it,
              and there is no NTP box above it any more, for the same reason:
              a save is a record, and a chassis write is an act somebody chose.
              The time servers live on Settings → NTP servers and are pushed by
              Sync on the Time card. */}
          <div className="sm:col-span-2">
            <Field
              label="Timezone"
              hint="The clock this OLT's schedules are read in — its nightly config backup and drift check run at the configured hour HERE, not on the server. Writing the zone to the chassis itself is a separate step on the Time card."
            >
              <TimezoneSelect
                value={form.timezone}
                onChange={(timezone) => setForm({ ...form, timezone })}
              />
            </Field>
          </div>
          <p className="text-xs text-faint sm:col-span-2">
            Time servers are set once for the platform on Settings → NTP servers, and written to
            this chassis by Sync NTP on the Time card. Saving here never touches the chassis.
          </p>

          {/*
            One switch, and the label has to carry which of three things it
            covers — otherwise an operator has to read the source to find out
            what they just turned off. What it hides: the unseen-event count in
            the Events column, and the triangle after the name. What it leaves
            alone, said out loud because both are easy to assume: the events
            themselves, which keep being recorded and stay on this OLT's own
            Events tab, and the alarms, which are findings and reach the bell
            whatever this says.
          */}
          <div className="sm:col-span-2">
            <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line bg-surface-2 px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-0.5 size-4 accent-primary"
                checked={form.eventNotifications}
                onChange={(e) => setForm({ ...form, eventNotifications: e.target.checked })}
              />
              <span className="text-xs text-muted">
                <span className="block text-sm text-ink">
                  Show event notifications for this OLT on the OLT list
                </span>
                Off hides this chassis&rsquo;s unseen-event count and the warning triangle beside
                its name. Its events are still recorded and still listed on its own Events tab, and
                genuine alarms — unreachable chassis, failed backup, optical findings — still reach
                the notification bell either way.
              </span>
            </label>
          </div>
        </div>
      </form>
    </WizardDialog>
  );
}

// --- cards --------------------------------------------------------------------

/**
 * Card service states across both vendors. ZTE reports INSERVICE / STANDBY /
 * OFFLINE; Huawei reports Normal / Active_normal / Standby_normal / Failed.
 * Healthy states are green — amber is reserved for states that genuinely need
 * attention, red for failure.
 */
function cardStatusTone(status: string): 'success' | 'warn' | 'danger' {
  if (/fail|fault|offline|invalid|abnormal/i.test(status)) return 'danger';
  if (/^inservice$/i.test(status) || /normal/i.test(status) || /^standby$/i.test(status)) {
    return 'success';
  }
  return 'warn';
}

type CardsResponse = { items: OltCard[]; warnings: string[]; fetchedAt?: number };

function CardsTab({ oltId }: { oltId: string }) {
  const queryClient = useQueryClient();
  // Served from the stored snapshot — visiting this tab never pulls from the
  // chassis. Refresh reads live and replaces the snapshot.
  const query = useQuery({
    queryKey: ['olt', oltId, 'cards'],
    queryFn: () => api.get<CardsResponse>(`/olts/${encodeURIComponent(oltId)}/cards`),
  });
  const refresh = useMutation({
    mutationFn: () => api.get<CardsResponse>(`/olts/${encodeURIComponent(oltId)}/cards?refresh=1`),
    onSuccess: (fresh) => queryClient.setQueryData(['olt', oltId, 'cards'], fresh),
  });

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }
  if (query.error) return <ErrorNotice error={query.error} />;

  const cards = query.data?.items ?? [];
  return (
    <div className="space-y-4">
      {(query.data?.warnings ?? []).map((warning) => (
        <div
          key={warning}
          className="rounded-lg border border-warn/30 bg-warn-soft px-4 py-2.5 text-sm text-warn"
        >
          {warning}
        </div>
      ))}
      <Card
        title={`Cards (${cards.length})`}
        action={
          <div className="flex items-center gap-3">
            {query.data?.fetchedAt !== undefined && (
              <span className="text-xs text-faint">
                updated {formatRelative(query.data.fetchedAt)}
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCw className="size-3.5" />}
              loading={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              Refresh
            </Button>
          </div>
        }
      >
        {cards.length === 0 ? (
          <EmptyState
            icon={<Cpu className="size-8" />}
            title="No cards detected"
            description="The interface table was empty — check SNMP access."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Slot</th>
              <th className="th">Type</th>
              <th className="th">Model</th>
              <th className="th">Status</th>
              <th className="th">Ports</th>
              <th className="th">Ports up</th>
              <th className="th">CPU</th>
              <th className="th">Memory</th>
            </THead>
            <TBody>
              {cards.map((card) => (
                <TRow key={`${card.shelf}/${card.slot}`}>
                  <td className="td font-mono text-xs">
                    {card.shelf}/{card.slot}
                  </td>
                  <td className="td">
                    <Pill tone={card.kind === 'pon' ? 'primary' : 'info'}>
                      {card.kind.toUpperCase()}
                    </Pill>
                  </td>
                  <td className="td">
                    {card.model ?? '—'}
                    {card.name && card.name !== card.model && (
                      <span className="ml-1.5 text-xs text-faint">({card.name})</span>
                    )}
                  </td>
                  <td className="td">
                    {card.status ? (
                      <Pill state tone={cardStatusTone(card.status)}>{card.status.toLowerCase()}</Pill>
                    ) : (
                      <span className="text-faint">—</span>
                    )}
                  </td>
                  <td className="td tabular-nums">{card.portCount}</td>
                  <td className="td tabular-nums">
                    <span className={card.portsUp > 0 ? 'text-success' : 'text-faint'}>
                      {card.portsUp}
                    </span>
                  </td>
                  {/* Over SNMP where the chassis exposes it (the ZTE C6xx); a dash elsewhere. */}
                  <td className="td tabular-nums">
                    {card.cpuPercent != null ? `${card.cpuPercent}%` : <span className="text-faint">—</span>}
                  </td>
                  <td className="td tabular-nums">
                    {card.memoryPercent != null ? `${card.memoryPercent}%` : <span className="text-faint">—</span>}
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// --- dialogs ------------------------------------------------------------------
// Every dialog on this page wears the shared WizardDialog shell.

/**
 * Runs a CLI-backed change the honest way: the exact commands are fetched as a
 * dry run and shown first, and the operator retypes the target to arm the
 * button. The dry-run plan and the confirm-word ceremony stay in-dialog because
 * they are read-only and shown before the write; but "Run" CLOSES the dialog
 * and hands the chassis write to the background task tray (like Resync and the
 * WAN wizard), so the operator is not pinned to a spinner then a transcript they
 * must dismiss. The full transcript is persisted to the action log, so the
 * receipt is not lost — the toast just reports whether the OLT warned.
 */
function CliActionDialog({
  title,
  consequence,
  confirmWord,
  planKey,
  plan,
  execute,
  invalidate,
  onClose,
}: {
  title: string;
  consequence: string;
  confirmWord: string;
  planKey: string;
  plan: () => Promise<CliActionResult>;
  execute: () => Promise<CliActionResult>;
  /** Query keys to refresh once the write lands — each call site's own data. */
  invalidate: QueryKey[];
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [typed, setTyped] = useState('');

  const planQuery = useQuery({ queryKey: ['cli-plan', planKey], queryFn: plan, staleTime: 0 });

  const armed = typed.trim().toLowerCase() === confirmWord.trim().toLowerCase();

  // Close and hand the real run to the tray. The transcript is captured in the
  // action log; here the toast only needs to say whether the OLT warned.
  const start = () => {
    onClose();
    tasks.start<CliActionResult>({
      label: title,
      run: execute,
      done: (r) => (r.warnings.length ? `done, ${r.warnings.length} warning(s)` : 'done'),
      invalidate,
    });
  };

  return (
    <WizardDialog
      title={title}
      subtitle={consequence}
      onClose={onClose}
      maxWidth="2xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!armed || planQuery.isLoading} onClick={start}>
            Run
          </Button>
        </div>
      }
    >
      {planQuery.isLoading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : planQuery.error ? (
        <ErrorNotice error={planQuery.error} />
      ) : (
        <>
          <Field label={`Type "${confirmWord}" to confirm`}>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmWord}
              autoFocus
            />
          </Field>
        </>
      )}
    </WizardDialog>
  );
}

// --- PON ports ----------------------------------------------------------------

type PonAction = { port: PonPort; action: 'enable' | 'disable' | 'restart-onus' };

function PonTab({ oltId }: { oltId: string }) {
  const { can } = useSession();
  const [editing, setEditing] = useState<PonPort | null>(null);
  const [optics, setOptics] = useState<PonPort | null>(null);
  const [acting, setActing] = useState<PonAction | null>(null);
  const [cardRestart, setCardRestart] = useState<{ slot: string; onuCount: number } | null>(null);
  const [cardLimit, setCardLimit] = useState<{ slot: string; current: number | null } | null>(null);

  const query = useQuery({
    queryKey: ['olt', oltId, 'pon'],
    queryFn: () => api.get<Page<PonPort>>(`/olts/${encodeURIComponent(oltId)}/pon-ports`),
  });
  const cardsQuery = useQuery({
    queryKey: ['olt', oltId, 'cards'],
    queryFn: () =>
      api.get<{ items: OltCard[]; warnings: string[] }>(`/olts/${encodeURIComponent(oltId)}/cards`),
  });
  const cardsBySlot = useMemo(
    () => new Map((cardsQuery.data?.items ?? []).map((c) => [`${c.shelf}/${c.slot}`, c])),
    [cardsQuery.data],
  );

  const groups = useMemo(() => {
    const map = new Map<string, PonPort[]>();
    for (const port of query.data?.items ?? []) {
      const slot = port.port.split('/').slice(0, 2).join('/');
      map.set(slot, [...(map.get(slot) ?? []), port]);
    }
    // Ports run 1..16 within a card, so sort by the port number, not the string.
    for (const ports of map.values()) {
      ports.sort((a, b) => Number(a.port.split('/')[2]) - Number(b.port.split('/')[2]));
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  }, [query.data]);

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }
  if (groups.length === 0) {
    return (
      <Card>
        <EmptyState
          title="No PON ports discovered yet"
          description="Run Sync inventory to walk the interface table."
        />
      </Card>
    );
  }

  const actionLabel = (action: PonAction['action']) =>
    action === 'restart-onus' ? 'Restart ONUs' : action === 'enable' ? 'Enable port' : 'Disable port';
  const cardTitle = (slot: string, ports: PonPort[]) => {
    const card = cardsBySlot.get(slot);
    return card?.model
      ? `Card ${slot} — ${card.model} (${card.portCount} Port)`
      : `Card ${slot} — ${ports.length} PON ports`;
  };

  return (
    <div className="space-y-5">
      {groups.map(([slot, ports]) => (
        <Card
          key={slot}
          title={cardTitle(slot, ports)}
          action={
            can('olt.manage') ? (
              <span className="flex gap-2">
                <Button
                  size="sm"
                  icon={<Pencil className="size-3.5" />}
                  onClick={() => setCardLimit({ slot, current: ports[0]?.maxOnus ?? null })}
                >
                  ONU limit
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  icon={<RotateCcw className="size-3.5" />}
                  onClick={() =>
                    setCardRestart({
                      slot,
                      onuCount: ports.reduce((sum, p) => sum + p.onuCount, 0),
                    })
                  }
                >
                  Restart card ONUs
                </Button>
              </span>
            ) : undefined
          }
        >
          <Table>
            <THead>
              <th className="th">Port</th>
              <th className="th">Label</th>
              <th className="th">Admin</th>
              <th className="th">Status</th>
              <th className="th">ONUs</th>
              <th className="th">Load</th>
              <th className="th">Tx power</th>
              {can('olt.manage') && <th className="th w-32">Actions</th>}
            </THead>
            <TBody>
              {ports.map((port) => {
                const capacity = port.maxOnus ?? 128;
                const pct = Math.min(100, Math.round((port.onuCount / capacity) * 100));
                return (
                  <TRow key={port.id}>
                    <td className="td font-mono text-xs">{port.port}</td>
                    <td className="td text-muted">{port.description ?? '—'}</td>
                    <td className="td">
                      <Pill state tone={port.adminStatus === 'enabled' ? 'success' : 'neutral'}>
                        {port.adminStatus}
                      </Pill>
                    </td>
                    <td className="td">
                      <Pill state tone={port.operStatus === 'up' ? 'success' : 'danger'} dot>
                        {port.operStatus}
                      </Pill>
                    </td>
                    <td className="td tabular-nums">
                      <span className="text-success">{port.onuOnlineCount}</span>
                      <span className="text-faint"> / {port.onuCount}</span>
                    </td>
                    <td className="td w-40">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-3">
                          <div
                            className={pct > 85 ? 'h-full bg-danger' : 'h-full bg-primary'}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-faint">
                          {port.onuCount}/{capacity}
                        </span>
                      </div>
                    </td>
                    {/* The port's own SFP, over SNMP on ZTE and the CLI on Huawei; the
                        figure opens its 15-minute history. */}
                    <td className="td tabular-nums text-xs">
                      {port.txPowerDbm != null ? (
                        <button
                          type="button"
                          className="text-link hover:underline"
                          title="Transmit power and module temperature over time"
                          onClick={() => setOptics(port)}
                        >
                          {port.txPowerDbm.toFixed(2)} dBm
                        </button>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    {can('olt.manage') && (
                      <td className="td">
                        <span className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Label and ONU limit"
                            icon={<Pencil className="size-3.5" />}
                            onClick={() => setEditing(port)}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            title={port.adminStatus === 'enabled' ? 'Disable port' : 'Enable port'}
                            className={
                              port.adminStatus === 'enabled'
                                ? 'text-danger hover:text-danger'
                                : 'text-success hover:text-success'
                            }
                            icon={<Power className="size-3.5" />}
                            onClick={() =>
                              setActing({
                                port,
                                action: port.adminStatus === 'enabled' ? 'disable' : 'enable',
                              })
                            }
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Restart every ONU on this port"
                            className="text-danger hover:text-danger"
                            icon={<RotateCcw className="size-3.5" />}
                            onClick={() => setActing({ port, action: 'restart-onus' })}
                          />
                        </span>
                      </td>
                    )}
                  </TRow>
                );
              })}
            </TBody>
          </Table>
        </Card>
      ))}

      {editing && <PonPortEditDialog oltId={oltId} port={editing} onClose={() => setEditing(null)} />}
      {optics && <PonPortOpticsDialog oltId={oltId} port={optics.port} onClose={() => setOptics(null)} />}
      {cardLimit && (
        <CardLimitDialog oltId={oltId} card={cardLimit} onClose={() => setCardLimit(null)} />
      )}
      {cardRestart && (
        <CliActionDialog
          title={`Restart every ONU on card ${cardRestart.slot}`}
          consequence={`All ${cardRestart.onuCount} ONUs across this card's PON ports reboot — every subscriber on the card drops for a couple of minutes.`}
          confirmWord={cardRestart.slot}
          planKey={`card-${cardRestart.slot}-restart`}
          plan={() =>
            api.post<CliActionResult>(`/olts/${encodeURIComponent(oltId)}/cards/action`, {
              shelf: Number(cardRestart.slot.split('/')[0]),
              slot: Number(cardRestart.slot.split('/')[1]),
              action: 'restart-onus',
              dryRun: true,
            })
          }
          execute={() =>
            api.post<CliActionResult>(`/olts/${encodeURIComponent(oltId)}/cards/action`, {
              shelf: Number(cardRestart.slot.split('/')[0]),
              slot: Number(cardRestart.slot.split('/')[1]),
              action: 'restart-onus',
              dryRun: false,
            })
          }
          invalidate={[['olt', oltId, 'pon']]}
          onClose={() => setCardRestart(null)}
        />
      )}
      {acting && (
        <CliActionDialog
          title={`${actionLabel(acting.action)} — gpon-olt_${acting.port.port}`}
          consequence={
            acting.action === 'disable'
              ? `Every subscriber on ${acting.port.port} (${acting.port.onuCount} ONUs) loses service until the port is re-enabled.`
              : acting.action === 'restart-onus'
                ? `All ${acting.port.onuCount} ONUs on ${acting.port.port} reboot — subscribers drop for a couple of minutes.`
                : `Port ${acting.port.port} comes back up and its ONUs re-range.`
          }
          confirmWord={acting.port.port}
          planKey={`pon-${acting.port.id}-${acting.action}`}
          plan={() =>
            api.post<CliActionResult>(
              `/olts/${encodeURIComponent(oltId)}/pon-ports/${encodeURIComponent(acting.port.id)}/action`,
              { action: acting.action, dryRun: true },
            )
          }
          execute={() =>
            api.post<CliActionResult>(
              `/olts/${encodeURIComponent(oltId)}/pon-ports/${encodeURIComponent(acting.port.id)}/action`,
              { action: acting.action, dryRun: false },
            )
          }
          invalidate={[['olt', oltId, 'pon']]}
          onClose={() => setActing(null)}
        />
      )}
    </div>
  );
}

function CardLimitDialog({
  oltId,
  card,
  onClose,
}: {
  oltId: string;
  card: { slot: string; current: number | null };
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [limit, setLimit] = useState(card.current != null ? String(card.current) : '');

  // Close on submit; the write and its outcome live in the tray.
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const body = {
      shelf: Number(card.slot.split('/')[0]),
      slot: Number(card.slot.split('/')[1]),
      maxOnus: limit ? Number(limit) : null,
    };
    onClose();
    tasks.start({
      label: `ONU limit — card ${card.slot}`,
      run: () => api.patch(`/olts/${encodeURIComponent(oltId)}/cards/pon-limit`, body),
      done: () =>
        body.maxOnus == null
          ? `Card ${card.slot}: limit cleared on every port.`
          : `Card ${card.slot}: ${body.maxOnus} ONUs per port on every port.`,
      invalidate: [['olt', oltId, 'pon']],
    });
  };

  return (
    <WizardDialog
      title={`ONU limit for card ${card.slot}`}
      subtitle="Applies to every PON port on this card"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="card-limit-form" variant="primary">
            Apply
          </Button>
        </div>
      }
    >
      <form id="card-limit-form" onSubmit={onSubmit}>
        <Field
          label="ONUs per port"
          hint="Provisioning limit used by the load bars and authorisation checks — the OLT's own hardware limit is not changed. Blank clears it (default 128)."
        >
          <Input
            type="number"
            min={1}
            max={256}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="64"
            autoFocus
          />
        </Field>
      </form>
    </WizardDialog>
  );
}

function PonPortEditDialog({
  oltId,
  port,
  onClose,
}: {
  oltId: string;
  port: PonPort;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [form, setForm] = useState({
    description: port.description ?? '',
    maxOnus: port.maxOnus != null ? String(port.maxOnus) : '',
  });

  // Close on submit; the write and its outcome live in the tray.
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const body = {
      description: form.description.trim() || null,
      maxOnus: form.maxOnus ? Number(form.maxOnus) : null,
    };
    onClose();
    tasks.start({
      label: `PON port ${port.port}`,
      run: () =>
        api.patch(
          `/olts/${encodeURIComponent(oltId)}/pon-ports/${encodeURIComponent(port.id)}`,
          body,
        ),
      done: () => `PON port ${port.port} saved.`,
      invalidate: [['olt', oltId, 'pon']],
    });
  };

  return (
    <WizardDialog
      title={`PON port ${port.port}`}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="pon-port-form" variant="primary">
            Save
          </Button>
        </div>
      }
    >
      <form id="pon-port-form" className="space-y-4" onSubmit={onSubmit}>
        <Field label="Label" hint="Shown in the port list — e.g. the feeder or area it serves">
          <Input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Gachie feeder 1"
            autoFocus
          />
        </Field>
        <Field
          label="ONU limit"
          hint="Provisioning limit used by this platform's load bar and authorisation checks — the OLT's own hardware limit is not changed"
        >
          <Input
            type="number"
            min={1}
            max={256}
            value={form.maxOnus}
            onChange={(e) => setForm({ ...form, maxOnus: e.target.value })}
            placeholder="128"
          />
        </Field>
      </form>
    </WizardDialog>
  );
}

// --- uplinks ------------------------------------------------------------------

interface UplinkDetail {
  ifName: string;
  mode: string | null;
  pvid: number | null;
  taggedVlans: number[];
  untaggedVlans: number[];
}

function UplinksTab({ oltId }: { oltId: string }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [acting, setActing] = useState<{ uplink: UplinkPort; action: 'enable' | 'disable' } | null>(null);
  const [vlanEdit, setVlanEdit] = useState<UplinkPort | null>(null);

  // Both served from stored snapshots — visiting this tab never pulls from
  // the chassis. Refresh reads both live and replaces the snapshots.
  const query = useQuery({
    queryKey: ['olt', oltId, 'uplinks'],
    queryFn: () =>
      api.get<{ items: UplinkPort[]; warnings: string[]; fetchedAt?: number }>(
        `/olts/${encodeURIComponent(oltId)}/uplinks`,
      ),
  });
  const vlanSummary = useQuery({
    queryKey: ['olt', oltId, 'uplink-vlan-summary'],
    queryFn: () =>
      api.get<{ items: UplinkVlanSummary[]; warnings: string[]; fetchedAt?: number }>(
        `/olts/${encodeURIComponent(oltId)}/uplinks/vlan-summary`,
      ),
    enabled: (query.data?.items.length ?? 0) > 0,
  });
  const refresh = useMutation({
    mutationFn: async () => {
      const ports = await api.get<{ items: UplinkPort[]; warnings: string[]; fetchedAt?: number }>(
        `/olts/${encodeURIComponent(oltId)}/uplinks?refresh=1`,
      );
      const vlans = await api.get<{
        items: UplinkVlanSummary[];
        warnings: string[];
        fetchedAt?: number;
      }>(`/olts/${encodeURIComponent(oltId)}/uplinks/vlan-summary?refresh=1`);
      return { ports, vlans };
    },
    onSuccess: ({ ports, vlans }) => {
      queryClient.setQueryData(['olt', oltId, 'uplinks'], ports);
      queryClient.setQueryData(['olt', oltId, 'uplink-vlan-summary'], vlans);
    },
  });
  const vlansByName = useMemo(
    () => new Map((vlanSummary.data?.items ?? []).map((v) => [v.ifName, v])),
    [vlanSummary.data],
  );

  const uplinks = useMemo(
    () =>
      // The ports that carry the network first: highest capacity, then name.
      [...(query.data?.items ?? [])].sort(
        (a, b) =>
          (b.speedMbps ?? 0) - (a.speedMbps ?? 0) ||
          a.name.localeCompare(b.name, undefined, { numeric: true }),
      ),
    [query.data],
  );

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }
  if (query.error) return <ErrorNotice error={query.error} />;

  return (
    <div className="space-y-4">
      <Card
        title={`Uplink ports (${uplinks.length})`}
        action={
          <div className="flex items-center gap-3">
            {query.data?.fetchedAt !== undefined && (
              <span className="text-xs text-faint">
                updated {formatRelative(query.data.fetchedAt)}
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              icon={<RefreshCw className="size-3.5" />}
              loading={refresh.isPending}
              onClick={() => refresh.mutate()}
            >
              Refresh
            </Button>
          </div>
        }
      >
        {uplinks.length === 0 ? (
          <EmptyState title="No uplink ports found" description="The interface table had no gei/xgei/eth ports." />
        ) : (
          <Table>
            <THead>
              <th className="th">Port</th>
              <th className="th">Description</th>
              <th className="th">Admin</th>
              <th className="th">Link</th>
              <th className="th">Speed</th>
              <th className="th">Current VLANs</th>
              {can('olt.manage') && <th className="th w-28">Actions</th>}
            </THead>
            <TBody>
              {uplinks.map((uplink) => {
                const vlans = vlansByName.get(uplink.name);
                return (
                  <TRow key={uplink.ifIndex}>
                    <td className="td font-mono text-xs">{uplink.name}</td>
                    <td className="td text-muted">{uplink.description ?? '—'}</td>
                    <td className="td">
                      <Pill state tone={uplink.adminStatus === 'enabled' ? 'success' : 'neutral'}>
                        {uplink.adminStatus}
                      </Pill>
                    </td>
                    <td className="td">
                      <Pill state tone={uplink.operStatus === 'up' ? 'success' : 'danger'} dot>
                        {uplink.operStatus}
                      </Pill>
                    </td>
                    <td className="td tabular-nums text-muted">
                      {uplink.speedMbps === null
                        ? '—'
                        : uplink.speedMbps >= 1000
                          ? `${uplink.speedMbps / 1000} G`
                          : `${uplink.speedMbps} M`}
                    </td>
                    <td className="td max-w-56">
                      {vlans ? (
                        <button
                          className="block w-full truncate text-left font-mono text-xs text-muted hover:text-ink"
                          onClick={() => setVlanEdit(uplink)}
                          title={`Tagged: ${compressVlanRanges(vlans.taggedVlans) || 'none'}${
                            vlans.pvid != null ? ` · PVID ${vlans.pvid}` : ''
                          }`}
                        >
                          {vlans.taggedVlans.length > 0
                            ? compressVlanRanges(vlans.taggedVlans)
                            : '—'}
                          {vlans.pvid != null && (
                            <span className="ml-1.5 text-faint">pvid {vlans.pvid}</span>
                          )}
                        </button>
                      ) : vlanSummary.isLoading ? (
                        <Spinner className="size-3.5" />
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    {can('olt.manage') && (
                      <td className="td">
                        <span className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            title="View and edit VLANs"
                            icon={<Pencil className="size-3.5" />}
                            onClick={() => setVlanEdit(uplink)}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            title={uplink.adminStatus === 'enabled' ? 'Disable port' : 'Enable port'}
                            className={
                              uplink.adminStatus === 'enabled'
                                ? 'text-danger hover:text-danger'
                                : 'text-success hover:text-success'
                            }
                            icon={<Power className="size-3.5" />}
                            onClick={() =>
                              setActing({
                                uplink,
                                action: uplink.adminStatus === 'enabled' ? 'disable' : 'enable',
                              })
                            }
                          />
                        </span>
                      </td>
                    )}
                  </TRow>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>
      <p className="text-xs text-faint">
        VLAN membership is read from the running config over the CLI — this firmware does not map it
        over SNMP.
      </p>

      {acting && (
        <CliActionDialog
          title={`${acting.action === 'enable' ? 'Enable' : 'Disable'} ${acting.uplink.name}`}
          consequence={
            acting.action === 'disable'
              ? `Everything carried by ${acting.uplink.name} — including subscriber traffic and possibly this platform's own management path — goes down.`
              : `${acting.uplink.name} comes back up.`
          }
          confirmWord={acting.uplink.name}
          planKey={`uplink-${acting.uplink.name}-${acting.action}`}
          plan={() =>
            api.post<CliActionResult>(`/olts/${encodeURIComponent(oltId)}/uplinks/action`, {
              ifName: acting.uplink.name,
              action: acting.action,
              dryRun: true,
            })
          }
          execute={() =>
            api.post<CliActionResult>(`/olts/${encodeURIComponent(oltId)}/uplinks/action`, {
              ifName: acting.uplink.name,
              action: acting.action,
              dryRun: false,
            })
          }
          invalidate={[['olt', oltId, 'uplinks']]}
          onClose={() => setActing(null)}
        />
      )}
      {vlanEdit && (
        <UplinkVlanDialog oltId={oltId} uplink={vlanEdit} onClose={() => setVlanEdit(null)} />
      )}
    </div>
  );
}

function UplinkVlanDialog({
  oltId,
  uplink,
  onClose,
}: {
  oltId: string;
  uplink: UplinkPort;
  onClose: () => void;
}) {
  const { can } = useSession();
  const tasks = useTasks();
  const [pvid, setPvid] = useState('');
  const [addTagged, setAddTagged] = useState('');
  const [removeTagged, setRemoveTagged] = useState<Set<number>>(new Set());
  const [confirming, setConfirming] = useState(false);

  // The running config is the source of truth for membership on this firmware.
  const detailQuery = useQuery({
    queryKey: ['olt', oltId, 'uplink-detail', uplink.name],
    queryFn: () =>
      api.get<UplinkDetail>(`/olts/${encodeURIComponent(oltId)}/uplinks/detail`, {
        ifName: uplink.name,
      }),
  });
  const detail = detailQuery.data;

  const parseVlans = (text: string): number[] =>
    text
      .split(/[\s,]+/)
      .map((v) => Number(v))
      .filter((v) => Number.isInteger(v) && v >= 1 && v <= 4094);

  const body = () => ({
    ifName: uplink.name,
    ...(pvid ? { pvid: Number(pvid) } : {}),
    addTagged: parseVlans(addTagged),
    removeTagged: [...removeTagged],
  });

  // Apply CLOSES the dialog and hands the write to the background tray. The
  // payload is snapshotted from the reviewed inputs before the dialog unmounts,
  // so the run in the tray is exactly what the confirm showed. The
  // transcript is persisted to the action log; the toast reports the warnings.
  const startApply = () => {
    const payload = { ...body(), dryRun: false };
    onClose();
    tasks.start<CliActionResult>({
      label: `VLANs on ${uplink.name}`,
      run: () =>
        api.post<CliActionResult>(`/olts/${encodeURIComponent(oltId)}/uplinks/vlans`, payload),
      done: (r) => (r.warnings.length ? `applied, ${r.warnings.length} warning(s)` : 'applied'),
      invalidate: [
        ['olt', oltId, 'uplink-detail', uplink.name],
        ['olt', oltId, 'uplink-vlan-summary'],
        ['olt', oltId, 'uplinks'],
      ],
    });
  };

  const toggleRemove = (vlan: number) => {
    // Any change to what will be sent re-arms the confirm, exactly as
    // olt/VlansTab.tsx does. `startApply` snapshots the inputs fresh at click
    // time; re-arming keeps the "are you sure" gate honest about the current
    // selection — disabling a subscriber-carrying VLAN on an uplink drops service.
    setConfirming(false);
    setRemoveTagged((prev) => {
      const next = new Set(prev);
      if (next.has(vlan)) next.delete(vlan);
      else next.add(vlan);
      return next;
    });
  };

  return (
    <WizardDialog
      title={`VLANs on ${uplink.name}`}
      subtitle={detail ? `Mode ${detail.mode ?? 'unknown'} · PVID ${detail.pvid ?? '—'}` : undefined}
      onClose={onClose}
      maxWidth="2xl"
      footer={
        <div className="flex items-center justify-end gap-2">
          {can('olt.manage') && detail && confirming ? (
            <span className="mr-auto text-sm text-muted">
              Are you sure? This runs on {uplink.name}.
            </span>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          {can('olt.manage') &&
            detail &&
            (confirming ? (
              <Button variant="danger" onClick={startApply}>
                Confirm
              </Button>
            ) : (
              <Button variant="danger" onClick={() => setConfirming(true)}>
                Apply
              </Button>
            ))}
        </div>
      }
    >
      <>
        {detailQuery.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner />
            <span className="ml-3 self-center text-sm text-muted">Reading the running config…</span>
          </div>
        ) : detailQuery.error ? (
          <ErrorNotice error={detailQuery.error} />
        ) : detail ? (
          <>
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-faint">
                Tagged VLANs ({detail.taggedVlans.length})
                {can('olt.manage') ? ' — click one to mark it for removal' : ''}
              </p>
              <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
                {detail.taggedVlans.length === 0 && <span className="text-sm text-faint">none</span>}
                {detail.taggedVlans.map((vlan) => (
                  <button
                    key={vlan}
                    type="button"
                    disabled={!can('olt.manage')}
                    onClick={() => toggleRemove(vlan)}
                    className={`rounded-full border px-2 py-0.5 font-mono text-xs transition-colors ${
                      removeTagged.has(vlan)
                        ? 'border-danger/50 bg-danger-soft text-danger line-through'
                        : 'border-line text-muted hover:text-ink'
                    }`}
                  >
                    {vlan}
                  </button>
                ))}
              </div>
              {detail.untaggedVlans.length > 0 && (
                <p className="mt-2 text-xs text-faint">
                  Untagged: {detail.untaggedVlans.join(', ')}
                </p>
              )}
            </div>

            {can('olt.manage') && (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Add tagged VLANs" hint="Comma separated, e.g. 200, 300">
                    <Input
                      value={addTagged}
                      onChange={(e) => {
                        setConfirming(false);
                        setAddTagged(e.target.value);
                      }}
                      placeholder="200, 300"
                      className="font-mono"
                    />
                  </Field>
                  <Field label="PVID" hint={`Currently ${detail.pvid ?? 'not set'} — blank keeps it`}>
                    <Input
                      type="number"
                      min={1}
                      max={4094}
                      value={pvid}
                      onChange={(e) => {
                        setConfirming(false);
                        setPvid(e.target.value);
                      }}
                      placeholder={detail.pvid != null ? String(detail.pvid) : 'unchanged'}
                    />
                  </Field>
                </div>
              </>
            )}
          </>
        ) : null}
      </>
    </WizardDialog>
  );
}

// --- ONUs ---------------------------------------------------------------------

function OnusTab({ oltId }: { oltId: string }) {
  const navigate = useNavigate();
  const { can } = useSession();
  const [search, setSearch] = useState('');
  const query = useQuery({
    queryKey: ['olt', oltId, 'onus', search],
    queryFn: () =>
      api.get<Page<Onu>>(`/olts/${encodeURIComponent(oltId)}/onus`, { search: search || undefined }),
  });

  const onus = query.data?.items ?? [];

  return (
    <>
    {can('olt.manage') && <DeferredRemovalsCard oltId={oltId} />}
    <Card
      title={`ONUs (${onus.length})`}
      action={
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Serial, name or index…"
          className="w-64"
        />
      }
    >
      {query.isLoading ? (
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      ) : onus.length === 0 ? (
        <EmptyState
          title="No ONUs discovered yet"
          description="Run Sync inventory. If it finds nothing, the vendor OID map needs verifying — use the Diagnostics tab."
        />
      ) : (
        <Table>
          <THead>
            <th className="th">State</th>
            <th className="th">Serial</th>
            <th className="th">Name</th>
            <th className="th">PON</th>
            <th className="th">ONU</th>
            <th className="th">Signal</th>
            <th className="th">OLT Rx</th>
            <th className="th">Distance</th>
            <th className="th">TR-069</th>
          </THead>
          <TBody>
            {onus.map((onu) => {
              const band = signalBand(onu.rxPowerDbm);
              return (
                <TRow
                  key={onu.id}
                  // §8.6.7: every ONU opens its own page, linked to a TR-069
                  // device or not — `onuPath` handles the no-device case, and
                  // the fleet list and Topology already navigate unconditionally.
                  // On gachie1, where 149 of 191 still point at SmartOLT's ACS,
                  // gating on a device link made most of the chassis's own list
                  // inert.
                  onClick={() => navigate(onuPath(onu))}
                  className="cursor-pointer"
                >
                  <td className="td">
                    <Pill state tone={onuHealthTone(onu)} dot>
                      {onuHealthLabel(onu)}
                    </Pill>
                  </td>
                  <td className="td font-mono text-xs">{onu.serial}</td>
                  <td className="td">{onu.name ?? '—'}</td>
                  <td className="td font-mono text-xs text-muted">{onu.ponPort || '—'}</td>
                  <td className="td font-mono text-xs text-muted">{onu.onuIndex}</td>
                  <td className="td">
                    {onu.rxPowerDbm === null ? (
                      <span className="text-faint">—</span>
                    ) : (
                      <Pill state tone={band.tone}>{onu.rxPowerDbm} dBm</Pill>
                    )}
                  </td>
                  <td className="td tabular-nums text-muted">
                    {onu.oltRxPowerDbm === null ? '—' : `${onu.oltRxPowerDbm} dBm`}
                  </td>
                  <td className="td tabular-nums text-muted">
                    {onu.distanceM === null ? '—' : `${onu.distanceM} m`}
                  </td>
                  <td className="td">
                    <Link
                      to={onuPath(onu)}
                      className="text-link hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open
                    </Link>
                  </td>
                </TRow>
              );
            })}
          </TBody>
        </Table>
      )}
    </Card>
    </>
  );
}

// --- events -------------------------------------------------------------------

interface OnuEvent {
  id: string;
  onuId: string;
  /** Subscriber name from the ONU row; null once that row is gone. */
  name: string | null;
  serial: string;
  interfaceName: string | null;
  kind: 'up' | 'down' | 'changed';
  cause: string;
  description: string;
  fromState: string | null;
  toState: string;
  at: number;
}

/** Red for a lost service, green for a recovery, amber for a refinement. */
const EVENT_TONES: Record<OnuEvent['kind'], 'danger' | 'success' | 'warn'> = {
  down: 'danger',
  up: 'success',
  changed: 'warn',
};

/**
 * Chassis removals still owed to this OLT.
 *
 * A delete takes the record at once and owes the chassis a `no onu` /
 * `ont delete`; when the OLT was not answering, or refused, that write sits in
 * `onu_deferred_deauthorizations` and retries for 48 hours. This is where an
 * operator sees what is still owed — and where they cancel one, because the
 * unit should stay authorised after all or somebody removed it by hand.
 * Hidden when nothing is owed: an empty card on every chassis is noise.
 */
function DeferredRemovalsCard({ oltId }: { oltId: string }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['olt', oltId, 'deferred-deauthorizations'],
    queryFn: () =>
      api.get<{ items: DeferredDeauthorization[] }>(
        `/olts/${encodeURIComponent(oltId)}/deferred-deauthorizations`,
      ),
    refetchInterval: 60_000,
  });
  const cancel = useMutation({
    mutationFn: (qid: string) =>
      api.delete<{ ok: boolean }>(
        `/olts/${encodeURIComponent(oltId)}/deferred-deauthorizations/${encodeURIComponent(qid)}`,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['olt', oltId, 'deferred-deauthorizations'] }),
  });

  const owed = (query.data?.items ?? []).filter(
    (r) => r.status === 'pending' || r.status === 'expired',
  );
  if (owed.length === 0) return null;

  return (
    <Card
      title={`Chassis removals owed (${owed.length})`}
      className="mb-5"
    >
      <p className="px-5 pt-4 text-xs text-muted">
        These ONUs are already deleted here. The OLT was not answering (or refused) when they
        were, so their removal from the chassis is queued and retried every two minutes for 48
        hours. Cancel one to leave the unit authorised on the chassis.
      </p>
      <Table>
        <THead>
          <th className="th">Serial</th>
          <th className="th">Slot</th>
          <th className="th">Status</th>
          <th className="th">Attempts</th>
          <th className="th">Last answer</th>
          <th className="th">Queued</th>
          <th className="th">Window</th>
          <th className="th" />
        </THead>
        <TBody>
          {owed.map((r) => (
            <TRow key={r.id}>
              <td className="td font-mono text-xs">{r.serial}</td>
              <td className="td font-mono text-xs">
                {r.ponPort}:{r.onuIndex}
              </td>
              <td className="td">
                <Pill state tone={r.status === 'expired' ? 'danger' : 'warn'}>
                  {r.status === 'expired' ? 'Expired — remove by hand' : 'Waiting for the OLT'}
                </Pill>
              </td>
              <td className="td text-xs">{r.attempts}</td>
              <td className="td max-w-xs truncate text-xs text-muted" title={r.lastError ?? undefined}>
                {r.lastError ?? '—'}
              </td>
              <td className="td text-xs text-muted" title={formatTimestamp(r.createdAt)}>
                {formatRelative(r.createdAt)}
                {r.actor ? ` · ${r.actor}` : ''}
              </td>
              <td className="td text-xs text-muted" title={formatTimestamp(r.expiresAt)}>
                {r.status === 'expired' ? 'closed' : `until ${formatRelative(r.expiresAt)}`}
              </td>
              <td className="td text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  loading={cancel.isPending && cancel.variables === r.id}
                  onClick={() => cancel.mutate(r.id)}
                  title="Stop the queued removal — the unit stays authorised on the chassis"
                >
                  Cancel removal
                </Button>
              </td>
            </TRow>
          ))}
        </TBody>
      </Table>
      {cancel.error && (
        <div className="px-5 pb-4">
          <ErrorNotice error={cancel.error} />
        </div>
      )}
    </Card>
  );
}

const WINDOWS = [
  { hours: 24, label: '24 hours' },
  { hours: 168, label: '7 days' },
  { hours: 720, label: '30 days' },
];

/**
 * Why an ONU went down, not merely that it did — a dying gasp means the
 * subscriber lost mains power, LOS points at the fibre.
 */
function EventsTab({ oltId }: { oltId: string }) {
  const queryClient = useQueryClient();
  const [hours, setHours] = useState(168);
  // `draft` is what is typed, `search` is what has been asked for. Splitting
  // them keeps a query off every keystroke — this scans a whole event history.
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const query = useQuery({
    queryKey: ['olt', oltId, 'events', hours, search],
    queryFn: () =>
      api.get<{ items: OnuEvent[]; total: number }>(
        `/olts/${encodeURIComponent(oltId)}/events?sinceHours=${hours}&limit=300${
          search.trim() ? `&search=${encodeURIComponent(search.trim())}` : ''
        }`,
      ),
  });

  /**
   * Opening this view is what marks the chassis read, and this is the only
   * place that says so.
   *
   * It is safe to hang off a mount BECAUSE of where this component sits: it is
   * rendered only while `tab === 'events'`, which means a person clicked the
   * Events tab or followed a link that asked for it. Nothing renders it in the
   * corner of a dashboard and nothing polls it — the 30-second refresh above
   * re-runs the QUERY, not this effect, so a page left open on the events tab
   * does not keep silently swallowing everything that arrives after.
   *
   * Deliberately fire-and-forget: a watermark that failed to save costs the
   * operator a count that did not clear, and interrupting them with an error
   * over that would be worse than the count.
   *
   * The list page's count is then invalidated so going Back shows the cleared
   * number rather than the one this click just answered.
   */
  useEffect(() => {
    let cancelled = false;
    api
      .post(`/olts/${encodeURIComponent(oltId)}/events/seen`)
      .then(() => {
        if (!cancelled) queryClient.invalidateQueries({ queryKey: ['olts'] });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [oltId, queryClient]);

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }
  if (query.error) return <ErrorNotice error={query.error} />;
  const items = query.data?.items ?? [];
  const powerFails = items.filter((e) => e.cause === 'power_fail').length;
  const fibreLoss = items.filter((e) => e.cause === 'fibre_loss').length;

  return (
    <Card
      title={`ONU events (${items.length})`}
      action={
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <span className="text-xs text-faint">
              {powerFails} power · {fibreLoss} fibre
            </span>
          )}
          {WINDOWS.map((w) => (
            <Button
              key={w.hours}
              size="sm"
              variant={hours === w.hours ? 'primary' : 'ghost'}
              onClick={() => setHours(w.hours)}
            >
              {w.label}
            </Button>
          ))}
        </div>
      }
    >
      <form
        className="flex max-w-lg items-center gap-2 border-b border-line px-4 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          setSearch(draft);
        }}
      >
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Name, serial, interface, cause or state…"
        />
        <Button type="submit" icon={<Search className="size-4" />} title="Search events">
          Search
        </Button>
        {search && (
          <Button
            type="button"
            variant="ghost"
            title="Clear the search"
            onClick={() => {
              setDraft('');
              setSearch('');
            }}
          >
            Clear
          </Button>
        )}
      </form>
      {items.length === 0 ? (
        <EmptyState
          title={search.trim() ? `Nothing matches "${search.trim()}"` : 'No state changes recorded yet'}
          description={
            search.trim()
              ? 'Try a subscriber name, a serial, a PON interface, a cause or a state.'
              : 'Events appear as syncs observe ONUs going down or coming back, with the cause the OLT reported.'
          }
        />
      ) : (
        <Table>
          <THead>
            <th className="th">Name</th>
            <th className="th">Timestamp</th>
            <th className="th">When</th>
            <th className="th">ONU</th>
            <th className="th">Change</th>
            <th className="th">Cause</th>
          </THead>
          <TBody>
            {items.map((e) => (
              <TRow key={e.id}>
                <td className="td font-medium">
                  {e.name ?? <span className="text-faint">—</span>}
                </td>
                <td className="td whitespace-nowrap font-mono text-xs text-muted">
                  {formatTimestamp(e.at)}
                </td>
                <td className="td whitespace-nowrap text-muted">{formatRelative(e.at)}</td>
                <td className="td">
                  <span className="font-mono text-xs">{e.interfaceName ?? e.serial}</span>
                </td>
                <td className="td">
                  <Pill state tone={EVENT_TONES[e.kind]}>
                    {e.fromState ? `${e.fromState} → ${e.toState}` : e.toState}
                  </Pill>
                </td>
                <td className="td text-sm text-muted">{e.description}</td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}


// --- config backups -----------------------------------------------------------

const BACKUP_KIND_TONES: Record<OltBackup['kind'], 'primary' | 'info' | 'warn' | 'neutral'> = {
  manual: 'primary',
  monthly: 'info',
  weekly: 'warn',
  daily: 'neutral',
};

const RESTORE_PHASE_TONES: Record<RestoreJob['phase'], 'info' | 'warn' | 'success' | 'danger'> = {
  applying: 'info',
  verifying: 'info',
  'awaiting-olt': 'warn',
  confirmed: 'success',
  failed: 'danger',
};

function BackupsTab({ olt }: { olt: Olt }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const tasks = useTasks();
  const [note, setNote] = useState('');
  const [viewing, setViewing] = useState<OltBackup | null>(null);
  const [deleting, setDeleting] = useState<OltBackup | null>(null);
  const [restoring, setRestoring] = useState<OltBackup | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['olt', olt.id, 'backups'],
    queryFn: () => api.get<Page<OltBackup>>(`/olts/${encodeURIComponent(olt.id)}/backups`),
  });
  // Poll while a restore is in flight so the operator watches it live.
  const restoreQuery = useQuery({
    queryKey: ['olt', olt.id, 'restore'],
    queryFn: () => api.get<{ job: RestoreJob | null }>(`/olts/${encodeURIComponent(olt.id)}/restore`),
    refetchInterval: (q) => {
      const phase = q.state.data?.job?.phase;
      return phase && !['confirmed', 'failed'].includes(phase) ? 3000 : false;
    },
  });
  const restoreJob = restoreQuery.data?.job ?? null;
  const backups = query.data?.items ?? [];
  const manualCount = backups.filter((b) => b.kind === 'manual').length;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['olt', olt.id, 'backups'] });

  const create = useMutation({
    mutationFn: () =>
      api.post(`/olts/${encodeURIComponent(olt.id)}/backups`, { note: note.trim() || null }),
    onSuccess: async () => {
      setNote('');
      setError(null);
      await refresh();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Backup failed'),
  });

  // The confirm closes on click and the delete runs in the tray.
  const startDelete = (backup: OltBackup) => {
    setDeleting(null);
    tasks.start({
      label: `Delete ${backup.kind} backup — ${formatTimestamp(backup.takenAt)}`,
      run: () =>
        api.delete(`/olts/${encodeURIComponent(olt.id)}/backups/${encodeURIComponent(backup.id)}`),
      done: () => `${backup.kind} backup from ${formatTimestamp(backup.takenAt)} deleted.`,
      invalidate: [['olt', olt.id, 'backups']],
    });
  };

  async function download(backup: OltBackup) {
    try {
      const response = await fetch(
        `/api/olts/${encodeURIComponent(olt.id)}/backups/${encodeURIComponent(backup.id)}/download`,
        { credentials: 'include' },
      );
      // The only raw fetch+blob download in the app, so it has to do what
      // lib/api does for everyone else. Without this check, the API's JSON
      // error body — a 502 when the config text is gone, or a plain 401 when
      // the session expired — was saved to disk AS the `.cfg`, and the
      // operator found out the backup was junk at restore time.
      if (!response.ok) {
        let message = `Download failed (${response.status})`;
        try {
          const body = (await response.json()) as { error?: { message?: string } };
          if (body?.error?.message) message = body.error.message;
        } catch {
          // A non-JSON error body (an nginx page, say) keeps the status line.
        }
        setError(message);
        return;
      }
      const blob = await response.blob();
      const stamp = platformIsoDate(backup.takenAt);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${olt.name.replace(/[^\w.-]+/g, '_')}-${stamp}-${backup.kind}.cfg`;
      link.click();
      URL.revokeObjectURL(link.href);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    }
  }

  return (
    <div className="space-y-4">
      {restoreJob && (
        <Card title="Restore in progress">
          <div className="space-y-2 p-5">
            <div className="flex items-center gap-3">
              <Pill state tone={RESTORE_PHASE_TONES[restoreJob.phase]}>
                {restoreJob.phase.replace('-', ' ')}
              </Pill>
              <span className="text-sm text-ink">
                {restoreJob.linesApplied}/{restoreJob.linesTotal} lines
              </span>
              {!['confirmed', 'failed'].includes(restoreJob.phase) && <Spinner className="size-4" />}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
              <div
                className={
                  restoreJob.phase === 'failed'
                    ? 'h-full bg-danger'
                    : restoreJob.phase === 'confirmed'
                      ? 'h-full bg-success'
                      : 'h-full bg-primary'
                }
                style={{
                  width: `${Math.round((restoreJob.linesApplied / Math.max(1, restoreJob.linesTotal)) * 100)}%`,
                }}
              />
            </div>
            <p
              className={`text-sm ${
                restoreJob.phase === 'failed'
                  ? 'text-danger'
                  : restoreJob.phase === 'confirmed'
                    ? 'text-success'
                    : 'text-muted'
              }`}
            >
              {restoreJob.message}
            </p>
            {restoreJob.errors.length > 0 && (
              <pre className="max-h-40 overflow-auto rounded-lg border border-warn/30 bg-warn-soft p-2 font-mono text-xs text-warn">
                {restoreJob.errors.join('\n')}
              </pre>
            )}
          </div>
        </Card>
      )}

      <Card
        title={`Config backups (${backups.length})`}
        action={
          can('olt.backup') ? (
            <div className="flex items-center gap-2">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note (e.g. before VLAN change)…"
                className="w-56"
              />
              <Button
                size="sm"
                variant="primary"
                icon={<DatabaseBackup className="size-3.5" />}
                loading={create.isPending}
                disabled={manualCount >= 5}
                onClick={() => create.mutate()}
              >
                Back up now ({manualCount}/5)
              </Button>
            </div>
          ) : undefined
        }
      >
        {query.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : backups.length === 0 ? (
          <EmptyState
            icon={<DatabaseBackup className="size-8" />}
            title="No backups yet"
            description="A daily backup is taken automatically after 2am; take a manual one before risky changes."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">Kind</th>
              <th className="th">Taken</th>
              <th className="th">Size</th>
              <th className="th">Note</th>
              <th className="th">By</th>
              <th className="th w-32">Actions</th>
            </THead>
            <TBody>
              {backups.map((backup) => (
                <TRow key={backup.id}>
                  <td className="td">
                    <Pill state={false} tone={BACKUP_KIND_TONES[backup.kind]}>{backup.kind}</Pill>
                  </td>
                  <td className="td text-muted">{formatTimestamp(backup.takenAt)}</td>
                  <td className="td tabular-nums text-muted">
                    {Math.round(backup.sizeBytes / 1024)} KB
                  </td>
                  <td className="td text-muted">{backup.note ?? '—'}</td>
                  <td className="td text-muted">{backup.createdBy ?? 'scheduler'}</td>
                  <td className="td">
                    <span className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        title="View config"
                        icon={<Eye className="size-3.5" />}
                        onClick={async () => {
                          try {
                            const full = await api.get<OltBackup>(
                              `/olts/${encodeURIComponent(olt.id)}/backups/${encodeURIComponent(backup.id)}`,
                            );
                            setViewing(full);
                            setError(null);
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Could not load the config');
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Download .cfg"
                        icon={<Download className="size-3.5" />}
                        onClick={() => void download(backup)}
                      />
                      {can('olt.restore') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Restore this config to the OLT"
                          className="text-warn hover:text-warn"
                          icon={<History className="size-3.5" />}
                          onClick={() => setRestoring(backup)}
                        />
                      )}
                      {can('olt.backup') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Delete backup"
                          className="text-danger hover:text-danger"
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => setDeleting(backup)}
                        />
                      )}
                    </span>
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
        {error && (
          <div className="border-t border-line p-4">
            <ErrorNotice error={new Error(error)} />
          </div>
        )}
      </Card>
      <p className="text-xs text-faint">
        Retention: the last 7 daily backups, one rolling weekly, one monthly taken on the 1st.
        Manual backups are never pruned automatically (up to 5 — delete them yourself). Restore
        replays into the running config only — nothing is written to flash until the OLT passes
        the health check, so a reboot always reverts to the previous configuration.
      </p>

      {viewing && (
        <WizardDialog
          title={`${viewing.kind} backup — ${formatTimestamp(viewing.takenAt)}`}
          subtitle={`${Math.round(viewing.sizeBytes / 1024)} KB`}
          onClose={() => setViewing(null)}
          maxWidth="2xl"
          bodyClassName="p-5"
          footer={
            <div className="flex justify-end gap-2">
              <Button onClick={() => void download(viewing)}>Download</Button>
              <Button onClick={() => setViewing(null)}>Close</Button>
            </div>
          }
        >
          <pre className="max-h-[60vh] overflow-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-ink">
            {viewing.content}
          </pre>
        </WizardDialog>
      )}
      {deleting && (
        <WizardDialog
          title={`Delete ${deleting.kind} backup?`}
          subtitle={`Taken ${formatTimestamp(deleting.takenAt)} — there is no undo.`}
          onClose={() => setDeleting(null)}
          footer={
            <div className="flex justify-end gap-2">
              <Button onClick={() => setDeleting(null)}>Cancel</Button>
              <Button variant="danger" onClick={() => startDelete(deleting)}>
                Delete
              </Button>
            </div>
          }
        >
          <p className="text-sm text-muted">
            The stored copy goes with it. A backup that is still needed can be downloaded first from
            its row.
          </p>
        </WizardDialog>
      )}
      {restoring && (
        <RestoreConfirmDialog olt={olt} backup={restoring} onClose={() => setRestoring(null)} />
      )}
    </div>
  );
}

const RESTORE_STEPS = [
  { key: 'review', title: 'Review' },
  { key: 'confirm', title: 'Confirm' },
];

/**
 * Two steps, because a config replay is the most service-affecting thing this
 * page can start: first what will happen and what protects the chassis if it
 * goes wrong, then the OLT's name typed back to arm it. Arming CLOSES the
 * dialog and hands the start to the tray; the restore job itself is watched
 * live by the Backups tab's own poll, which the tray's invalidate wakes.
 */
function RestoreConfirmDialog({
  olt,
  backup,
  onClose,
}: {
  olt: Olt;
  backup: OltBackup;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [step, setStep] = useState(0);
  const [typed, setTyped] = useState('');
  const armed = typed.trim() === olt.name;

  const start = () => {
    onClose();
    tasks.start({
      label: `Restore ${olt.name} — ${backup.kind} backup from ${formatTimestamp(backup.takenAt)}`,
      href: `/olts/${encodeURIComponent(olt.id)}?tab=backups`,
      run: () =>
        api.post(
          `/olts/${encodeURIComponent(olt.id)}/backups/${encodeURIComponent(backup.id)}/restore`,
        ),
      done: () =>
        `Restore started on ${olt.name}. Watch the Backups tab — nothing reaches flash until the health check passes.`,
      invalidate: [['olt', olt.id, 'restore']],
    });
  };

  return (
    <WizardDialog
      title={`Restore ${backup.kind} backup from ${formatTimestamp(backup.takenAt)}`}
      subtitle={`${Math.round(backup.sizeBytes / 1024)} KB replayed into ${olt.name}'s running config`}
      onClose={onClose}
      maxWidth="2xl"
      steps={RESTORE_STEPS}
      currentStep={step}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(0)}>
            Back
          </Button>
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            {step === 0 ? (
              <Button variant="primary" onClick={() => setStep(1)}>
                Next
              </Button>
            ) : (
              <Button variant="danger" disabled={!armed} onClick={start}>
                Restore
              </Button>
            )}
          </div>
        </div>
      }
    >
      {step === 0 ? (
        <>
          <p className="text-sm text-muted">
            The backup replays into the <span className="text-ink">running config only</span> —
            nothing is written to flash until the OLT passes the full health check afterwards.
            Because flash still holds the current configuration, a reboot or power-cycle reverts
            everything if the restore goes wrong. If the OLT does not come back within 15 minutes,
            the job fails itself and tells you exactly that.
          </p>
          <p className="rounded-lg border border-warn/40 bg-warn-soft px-3 py-2 text-sm text-warn">
            Replaying a config is service-affecting: lines that conflict with the current state can
            bounce subscribers while they apply. Do this in a maintenance window.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm text-muted">
            This starts the replay on <span className="text-ink">{olt.name}</span> as soon as you
            confirm. The dialog closes and the run is reported in the tasks tray; the Backups tab
            follows the job live.
          </p>
          <Field label={`Type "${olt.name}" to confirm`}>
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={olt.name}
              autoFocus
            />
          </Field>
        </>
      )}
    </WizardDialog>
  );
}

/** Runs every read and login the platform depends on, and shows each verdict. */
function HealthCard({ oltId }: { oltId: string }) {
  const [communitiesNote, setCommunitiesNote] = useState<string | null>(null);
  /**
   * Puts the platform's stored SNMP communities on the chassis over the CLI
   * (2026-09-18, Lagos: reads worked, writes timed out, the read-write
   * community was simply not on the C650). Re-runs the checks on success so
   * the write row turns green in front of the operator.
   */
  const applyCommunities = useMutation({
    mutationFn: () =>
      api.post<{ added: string[]; view: string; alreadyPresent: boolean; executed: boolean; warnings: string[] }>(
        `/olts/${encodeURIComponent(oltId)}/snmp/communities`,
      ),
    onSuccess: (r) => {
      setCommunitiesNote(
        r.alreadyPresent
          ? 'Both stored communities are already on the chassis — if writes still time out, the stored read-write value differs from the one the chassis holds.'
          : `Added ${r.added.map((k) => (k === 'rw' ? 'the read-write community' : 'the read community')).join(' and ')} under view "${r.view}" and saved. Re-running the checks…`,
      );
      if (!r.alreadyPresent) health.mutate();
    },
    onError: (err) => setCommunitiesNote(err instanceof Error ? err.message : 'Could not put the communities on the chassis.'),
  });
  const { can } = useSession();
  const queryClient = useQueryClient();
  // The scheduler stores a diagnosis every 6 hours, so the last state renders
  // instantly; "Run all checks" still runs live (45–90s on a big chassis).
  const stored = useQuery({
    queryKey: ['olt-health', oltId],
    queryFn: () => api.get<{ latest: OltHealth | null }>(`/olts/${encodeURIComponent(oltId)}/health`),
  });
  const health = useMutation({
    mutationFn: () => api.post<OltHealth>(`/olts/${encodeURIComponent(oltId)}/health`),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['olt-health', oltId], { latest: fresh });
    },
  });
  const result = health.data ?? stored.data?.latest ?? null;

  return (
    <Card
      title="Health check"
      action={
        can('olt.diagnose') ? (
          <Button
            size="sm"
            variant="primary"
            icon={<Activity className="size-3.5" />}
            loading={health.isPending}
            onClick={() => health.mutate()}
          >
            Run all checks
          </Button>
        ) : undefined
      }
    >
      {health.isPending ? (
        <div className="flex items-center justify-center gap-3 p-8 text-sm text-muted">
          <Spinner /> Running every check against the OLT — SNMP, CLI, inventory, optics… this
          takes a minute or two on a large chassis.
        </div>
      ) : health.error ? (
        <div className="p-5">
          <ErrorNotice error={health.error} />
        </div>
      ) : result ? (
        <>
          <div
            className={`flex items-baseline justify-between gap-3 border-b border-line px-5 py-3 text-sm ${
              result.ok ? 'text-success' : 'text-danger'
            }`}
          >
            <span>
              {result.ok
                ? 'Fully functional — every check the platform depends on passed.'
                : 'Not fully functional — fix the failed checks below.'}
            </span>
            {result.checkedAt && (
              <span className="shrink-0 text-xs text-faint">
                checked {formatRelative(result.checkedAt)}
              </span>
            )}
          </div>
          <div className="divide-y divide-line">
            {result.checks.map((check) => (
              <div key={check.id} className="flex items-start gap-3 px-5 py-2.5">
                <Pill state
                  tone={
                    check.ok === null
                      ? 'neutral'
                      : check.ok
                        ? 'success'
                        : check.optional
                          ? 'warn'
                          : 'danger'
                  }
                >
                  {check.ok === null ? 'skipped' : check.ok ? 'pass' : 'fail'}
                </Pill>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm text-ink">
                      {check.label}
                      {check.optional && (
                        <span className="ml-1.5 text-[11px] text-faint">optional</span>
                      )}
                    </span>
                    {check.latencyMs !== null && (
                      <span className="text-xs tabular-nums text-faint">
                        {formatLatency(check.latencyMs)}
                      </span>
                    )}
                  </div>
                  {check.detail && (
                    <p className={`text-xs ${check.ok === false ? 'text-danger' : 'text-muted'}`}>
                      {check.detail}
                    </p>
                  )}
                  {check.hint && <p className="text-xs text-warn">{check.hint}</p>}
                  {/* The one failed check with a button (2026-09-18): the stored
                      communities are put on the chassis over the CLI, then the
                      checks run again. ZTE only — the API refuses Huawei by name. */}
                  {check.id === 'snmp-write' && check.ok === false && can('olt.manage') && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        loading={applyCommunities.isPending}
                        onClick={() => applyCommunities.mutate()}
                      >
                        Put the stored communities on the chassis
                      </Button>
                      {communitiesNote && <span className="text-xs text-muted">{communitiesNote}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="p-5 text-sm text-muted">
          {stored.isLoading
            ? 'Loading the last stored result…'
            : 'No diagnosis stored yet — one runs automatically every 6 hours, or run all checks now. Each check runs for real: SNMP read and write, CLI login, card inventory, PON port and ONU discovery, the VLAN table, OLT-side optics and a running-config read.'}
        </p>
      )}
    </Card>
  );
}

/** Vendor OID maps vary by firmware train; this is how they get verified. */
function DiagnosticsTab({ oltId, vendor }: { oltId: string; vendor: Olt['vendor'] }) {
  const [oid, setOid] = useState(vendor === 'huawei' ? '1.3.6.1.4.1.2011' : '1.3.6.1.4.1.3902');
  const [rows, setRows] = useState<SnmpWalkRow[]>([]);
  const [truncated, setTruncated] = useState(false);

  const walk = useMutation({
    mutationFn: () =>
      api.get<{ oid: string; rows: SnmpWalkRow[]; truncated: boolean }>(
        `/olts/${encodeURIComponent(oltId)}/snmp-walk`,
        { oid, limit: 300 },
      ),
    onSuccess: (result) => {
      setRows(result.rows);
      setTruncated(result.truncated);
    },
  });

  return (
    <div className="space-y-5">
      <HealthCard oltId={oltId} />
      <Card title="SNMP walk">
        <div className="space-y-3 p-5">
          <p className="text-sm text-muted">
            ONU-level data has no standard MIB, so the vendor OIDs differ between firmware trains.
            Walk a subtree here to confirm what this OLT actually exposes; the results feed the map
            in <code className="font-mono text-xs">services/olt/oids.ts</code>.
          </p>
          <div className="flex gap-2">
            <Input
              value={oid}
              onChange={(e) => setOid(e.target.value)}
              placeholder="1.3.6.1.2.1.31.1.1.1.1"
              className="font-mono"
            />
            <Button
              variant="primary"
              icon={<Terminal className="size-4" />}
              loading={walk.isPending}
              onClick={() => walk.mutate()}
            >
              Walk
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            {[
              { label: 'Interface names', value: '1.3.6.1.2.1.31.1.1.1.1' },
              { label: 'System', value: '1.3.6.1.2.1.1' },
              {
                label: 'Vendor tree',
                value: vendor === 'huawei' ? '1.3.6.1.4.1.2011.6.128' : '1.3.6.1.4.1.3902.1012',
              },
            ].map((preset) => (
              <button
                key={preset.value}
                onClick={() => setOid(preset.value)}
                className="rounded-full border border-line px-2.5 py-1 text-muted hover:text-ink"
              >
                {preset.label}
              </button>
            ))}
          </div>
          {walk.error && <ErrorNotice error={walk.error} />}
        </div>
      </Card>

      {rows.length > 0 && (
        <Card
          title={`${rows.length} rows${
            truncated ? ' — truncated at the 300-row limit; narrow the OID to see the rest' : ''
          }`}
        >
          <div className="max-h-[32rem] overflow-auto">
            <Table>
              <THead>
                <th className="th">OID</th>
                <th className="th">Type</th>
                <th className="th">Value</th>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TRow key={row.oid}>
                    <td className="td font-mono text-xs">{row.oid}</td>
                    <td className="td text-xs text-muted">{row.type}</td>
                    <td className="td font-mono text-xs">{row.value}</td>
                  </TRow>
                ))}
              </TBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
